import React from "react";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

const cellText = (value) => (
  <MDTypography
    component="p"
    variant="button"
    color="text"
    fontWeight="regular"
  >
    {value}
  </MDTypography>
);

const cellCaption = (value) => (
  <MDTypography
    component="p"
    variant="caption"
    color="text"
    fontWeight="regular"
  >
    {value}
  </MDTypography>
);

const cellRequired = (value) => (
  <MDTypography
    component="p"
    variant="button"
    color="text"
    fontWeight="regular"
  >
    {value}
    <span style={{ color: "#ff4848" }}>*</span>
  </MDTypography>
);

const badge = (type) => (
  <MDTypography
    component="p"
    variant="caption"
    color="text"
    fontWeight="regular"
  >
    <MDBadge
      color="success"
      badgeContent={type}
      variant="gradient"
      size="sm"
      sx={{
        "& .MuiBadge-badge": {
          background: "#022DEC",
          backgroundImage: "none",
        },
      }}
    ></MDBadge>
  </MDTypography>
);

// ---------------------------------------------------------------------------
// Shared column definitions
// ---------------------------------------------------------------------------
const headerColumns = [
  { Header: "#", accessor: "num", align: "left", width: "6%" },
  { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
  { Header: "Value", accessor: "description", align: "left" },
];

const paramColumns = [
  { Header: "#", accessor: "num", align: "left", width: "6%" },
  { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
  { Header: "Value Description", accessor: "description", align: "left" },
];

const fieldColumns = [
  { Header: "#", accessor: "num", align: "left", width: "6%" },
  { Header: "Field", accessor: "fieldName", align: "left", width: "28%" },
  { Header: "Type", accessor: "dataType", align: "center", width: "14%" },
  { Header: "Description", accessor: "description", align: "left" },
];

const fieldColumnsWithRequired = [
  { Header: "#", accessor: "num", align: "left", width: "6%" },
  { Header: "Field", accessor: "fieldName", align: "left", width: "24%" },
  { Header: "Required", accessor: "paramType", align: "center", width: "12%" },
  { Header: "Type", accessor: "dataType", align: "center", width: "14%" },
  { Header: "Description", accessor: "description", align: "left" },
];

// ---------------------------------------------------------------------------
// Shared header rows
// ---------------------------------------------------------------------------
const contentTypeHeaderRows = [
  {
    num: cellText("1"),
    fieldName: cellText("Content-Type"),
    description: cellCaption("application/json"),
  },
];

const bearerHeaderRows = [
  {
    num: cellText("1"),
    fieldName: cellText("Content-Type"),
    description: cellCaption("application/json"),
  },
  {
    num: cellText("2"),
    fieldName: cellText("Authorization"),
    description: cellCaption("Bearer {your_token_here}"),
  },
];

// ---------------------------------------------------------------------------
// Shared response field tables
// ---------------------------------------------------------------------------
const dataSourceLoadOptionsNote =
  "Request parameters like Data Source Load Options follow DevExtreme conventions for filtering, paging, and sorting at the end of this document.";

const respFieldColumns = [
  { Header: "#", accessor: "num", align: "left", width: "6%" },
  { Header: "Field", accessor: "fieldName", align: "left", width: "26%" },
  { Header: "Type", accessor: "dataType", align: "center", width: "12%" },
  { Header: "Description", accessor: "description", align: "left" },
];

// Build response field rows from [field, type, description] tuples.
const respRows = (arr) =>
  arr.map(([field, type, desc], i) => ({
    num: cellText(String(i + 1)),
    fieldName: cellText(field),
    dataType: badge(type),
    description: cellCaption(desc),
  }));

const respTable = (rows) => ({ columns: respFieldColumns, rows: respRows(rows) });

// Standard RFC 7807 Problem Details error (used by most 400 / 404 responses).
const problemDetailsRows = [
  ["type", "String", "A URI reference that identifies the problem type (e.g., 'about:blank' for generic issues)."],
  ["title", "String", "A short, human-readable summary of the problem — typically indicates a validation failure."],
  ["status", "Integer", "HTTP status code — e.g., 400 means Bad Request."],
  ["detail", "String", "A specific explanation of the problem encountered."],
  ["instance", "String", "The request path (e.g., /risk_profiles) where the error occurred."],
  ["extensions", "Object", "Optional field that may include additional data; often empty."],
];

const problemDetailsCode = `{
  "Type": "about:blank",
  "Title": "title",
  "Status": 400,
  "Detail": "Title",
  "Instance": "/RiskProfile/policies",
  "Extensions": {}
}`;

const badRequestTable = {
  status: "Status Code: 400 (Bad Request)",
  statusClass: "code-red",
  code: problemDetailsCode,
  table: respTable(problemDetailsRows),
};

// Trader-style 400 (capitalized keys, simple title/status/detail/instance table).
const traderBadRequest = (detail, instance = "/traders", detailDesc) => ({
  status: "Status Code: 400 (Bad Request)",
  statusClass: "code-red",
  code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "${detail}",
  "Instance": "${instance}",
  "Extensions": {}
}`,
  table: respTable([
    ["title", "String", "Human-readable title of the error."],
    ["status", "Integer", "HTTP status code."],
    ["detail", "String", detailDesc || detail],
    ["instance", "String", "URI of the request that caused the error."],
  ]),
});

// Shared entity response field rows
const riskProfileRespRows = [
  ["id", "String", "Unique ID of the risk profile."],
  ["name", "String", "Name of the risk profile."],
  ["pre_trade_policies", "Array", "List of policies enforced before trade execution. Each contains cfg and action."],
  ["post_trade_policies", "Array", "List of policies enforced after trades. May include nested layers."],
  ["used_by", "Object", "Object containing firms, groups, and traders associated with this profile."],
  ["assigned_firm", "Integer", "ID of the assigned firm. 0 if none."],
  ["assigned_group", "Integer", "ID of the assigned group. 0 if none."],
  ["assigned_account", "Integer", "ID of the assigned account. 0 if none."],
  ["createdDate", "DateTime", "Timestamp when created."],
  ["updatedDate", "DateTime", "Timestamp when last updated."],
  ["totalCount", "Integer", "Total number of risk profiles available."],
  ["groupCount", "Integer", "Number of grouped records (often -1 if not grouped)."],
];

// Risk profile request body fields [field, required, type, description].
const riskProfileBodyData = [
  ["id", "Yes", "String", "Unique ID risk profile."],
  ["name", "Yes", "String", "Name of the risk profile."],
  ["pre_trade_policies[]", "Yes", "Array", "List of rules/policies to be enforced before trade execution."],
  ["id (pre_trade)", "Yes", "String", "Unique name for the policy group."],
  ["type (pre_trade)", "Yes", "Integer", "Numeric type code for the policy."],
  ["cfg (pre_trade)", "Yes", "Array", "Configuration key-value pairs."],
  ["action (pre_trade)", "Yes", "Object", "Defines what action to take when the condition is met (0 = reject, 1 = warning)."],
  ["post_trade_policies", "No", "Array", "List of rules/policies enforced after trades. Always structured with layers."],
  ["layers[] (post_trade)", "Yes", "Object", "Required for (POST-trade policy)."],
  ["layers[].id (post_trade)", "Yes", "String", "Unique name for the policy group."],
  ["layers[].type (post_trade)", "Yes", "Integer", "Numeric type code for the policy."],
  ["layers[].cfg (post_trade)", "Yes", "Object", "Array of key-value config settings for the policy."],
  ["layers[].action", "Yes", "Object", "Defines action to take when policy is triggered: 0 = reject (default), 1 = warning."],
];

const riskProfileBodyRows = riskProfileBodyData.map(([field, required, type, desc], i) => ({
  num: cellText(String(i + 1)),
  fieldName: required === "Yes" ? cellRequired(field) : cellText(field),
  paramType: cellCaption(required),
  dataType: badge(type),
  description: cellCaption(desc),
}));

// ---------------------------------------------------------------------------
// Risk Profile structural sub-tables (Create / Update Risk Profile)
// ---------------------------------------------------------------------------
const idTypeColumns = [
  { Header: "#", accessor: "num", align: "left", width: "8%" },
  { Header: "ID", accessor: "fieldName", align: "left", width: "60%" },
  { Header: "Type", accessor: "dataType", align: "center" },
];

const keyValueColumns = [
  { Header: "#", accessor: "num", align: "left", width: "8%" },
  { Header: "Key", accessor: "fieldName", align: "left", width: "30%" },
  { Header: "Value", accessor: "description", align: "left" },
];

const structFieldColumns = [
  { Header: "#", accessor: "num", align: "left", width: "8%" },
  { Header: "Field", accessor: "fieldName", align: "left", width: "26%" },
  { Header: "Type", accessor: "dataType", align: "center", width: "16%" },
  { Header: "Description", accessor: "description", align: "left" },
];

const postStructColumns = [
  { Header: "Parent", accessor: "num", align: "left", width: "14%" },
  { Header: "Field", accessor: "fieldName", align: "left", width: "20%" },
  { Header: "Type", accessor: "dataType", align: "center", width: "14%" },
  { Header: "Description", accessor: "description", align: "left" },
];

const mandatoryPoliciesTable = {
  columns: idTypeColumns,
  rows: [
    { num: cellText("1"), fieldName: cellText("Suspended"), dataType: cellText("4") },
    { num: cellText("2"), fieldName: cellText("Liquidation Only"), dataType: cellText("3") },
  ],
};

const preTradeStructTable = {
  columns: structFieldColumns,
  rows: [
    { num: cellText("1"), fieldName: cellRequired("id"), dataType: badge("String"), description: cellCaption("Unique name for the policy group.") },
    { num: cellText("2"), fieldName: cellRequired("type"), dataType: badge("Integer"), description: cellCaption("Numeric type code for the policy.") },
    { num: cellText("3"), fieldName: cellRequired("cfg"), dataType: badge("Object"), description: cellCaption("Array of key-value config settings for the policy.") },
    { num: cellText("4"), fieldName: cellRequired("action"), dataType: badge("Object"), description: cellCaption("Defines action to take when policy is triggered: 0 → reject (default), 1 → warning.") },
  ],
};

const postTradeStructTable = {
  columns: postStructColumns,
  rows: [
    { num: cellText("—"), fieldName: cellRequired("layers"), dataType: badge("Array"), description: cellCaption("Each policy can have layers.") },
    { num: cellText("layers[]"), fieldName: cellRequired("id"), dataType: badge("String"), description: cellCaption("Unique name for the policy group.") },
    { num: cellText("layers[]"), fieldName: cellRequired("type"), dataType: badge("Integer"), description: cellCaption("Numeric type code for the policy.") },
    { num: cellText("layers[]"), fieldName: cellRequired("cfg"), dataType: badge("Object"), description: cellCaption("Array of key-value configuration settings specific to the layer.") },
    { num: cellText("layers[]"), fieldName: cellRequired("action"), dataType: badge("Object"), description: cellCaption("Action Object (type: 0 = reject, 1 = warning) for this layer.") },
  ],
};

const cfgPreTradeExampleTable = {
  columns: keyValueColumns,
  rows: [
    { num: cellText("1"), fieldName: cellText("blacklist"), description: cellCaption("MSFT.A;AAPL;QQQ — List of semicolons separated restricted symbols") },
    { num: cellText("2"), fieldName: cellText("threshold"), description: cellCaption("Absolute limit (Float 0.0)") },
  ],
};

const cfgPostTradeExampleTable = {
  columns: keyValueColumns,
  rows: [
    { num: cellText("1"), fieldName: cellText("threshold"), description: cellCaption("Absolute limit (Float 0.0, ∞).") },
    { num: cellText("2"), fieldName: cellText("equity%"), description: cellCaption("Equity percentage (Float 0.0, 666).") },
  ],
};

const createRiskProfileExampleJson = `{
  "id": "PKUXKY10009",
  "name": "PKUXKY10009",
  "pre_trade_policies": [
    { "id": "Suspended", "type": 4, "cfg": [], "action": { "type": 0 } },
    { "id": "Liquidation Only", "type": 3, "cfg": [], "action": { "type": 0 } },
    {
      "id": "Options Symbols Buy Order QTY Limit #",
      "type": 39,
      "cfg": [
        { "key": "threshold", "val": "12222" },
        { "key": "symbols", "val": "IBM" }
      ],
      "action": { "type": 0 }
    },
    {
      "type": 33,
      "id": "symbols_blacklist",
      "layers": [
        {
          "action": { "type": 0 },
          "cfg": [{ "key": "blacklist", "val": "MSFT.A;AAPL;QQQ" }]
        }
      ]
    }
  ],
  "post_trade_policies": [
    {
      "type": 1,
      "id": "Loss Limit $",
      "layers": [
        {
          "action": { "type": 0 },
          "cfg": [{ "key": "threshold", "val": 122 }]
        }
      ]
    },
    {
      "type": 2,
      "id": "Loss Limit %",
      "layers": [
        {
          "action": { "type": 0 },
          "cfg": [{ "key": "equity%", "val": 12 }]
        }
      ]
    }
  ]
}`;

const mandatoryPoliciesJson = `{
  "pre_trade_policies": [
    { "id": "Suspended", "type": 4, "cfg": [], "action": { "type": 0 } },
    { "id": "Liquidation Only", "type": 3, "cfg": [], "action": { "type": 0 } }
  ]
}`;

const requiredBodyJson = `{
  "id": "ABC1234567",
  "name": "ABC1234567",
  "pre_trade_policies": [
    { "id": "Suspended", "type": 4, "cfg": [], "action": { "type": 0 } },
    { "id": "Liquidation Only", "type": 3, "cfg": [], "action": { "type": 0 } }
  ],
  "post_trade_policies": []
}`;

// Full 200-success response field rows for Create / Update Risk Profile (21 rows).
const riskProfileFullRespRows = [
  ["id", "String", "Unique ID Risk Profile."],
  ["name", "String", "Name of Risk Profile."],
  ["pre_trade_policies", "Array", "List of policies enforced before trade execution."],
  ["id (pre_trade)", "String", "Unique name of the pre-trade policy."],
  ["type (pre_trade)", "Integer", "Numeric type code for the policy."],
  ["cfg (pre_trade)", "Array", "Configuration key-value pairs (used when the rule is simple and not layered)."],
  ["action (pre_trade)", "Object", "Defines the action when the condition is met (e.g., 0 = reject, 1 = warning)."],
  ["post_trade_policies", "Array", "List of policies enforced after trades (always structured with layers)."],
  ["layers", "Object", "Required for (POST-trade policy)."],
  ["layers[].id (post_trade)", "String", "Unique name of the POST-trade policy."],
  ["layers[].type (post_trade)", "Integer", "Numeric type code for the policy."],
  ["layers[].cfg (post_trade)", "Object", "Config conditions specific to each layer."],
  ["layers[].action", "Object", "Enforcement action for a specific layer (0 = rejected, 1 = warning)."],
  ["used_by", "Object", "Object containing firms, groups, and traders associated with this policy."],
  ["firms", "String[]", "List of firm IDs for Risk Profile."],
  ["groups", "String[]", "List of group IDs for Risk Profile."],
  ["traders", "String[]", "List of trader IDs for Risk Profile."],
  ["assigned_firm", "Integer", "ID of the assigned firm. 0 if none."],
  ["assigned_group", "Integer", "ID of the assigned group. 0 if none."],
  ["assigned_account", "Integer", "ID of the assigned account. 0 if none."],
  ["createdDate", "DateTime", "Timestamp when created."],
];

const riskProfile400Table = {
  status: "Status Code: 400 (Bad Request)",
  statusClass: "code-red",
  code: `{
  "errors": {
    "pre_trade_policies": [
      "The pre_trade_policies field is required."
    ]
  },
  "type": " ",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "traceId": "00-b19fd43cc20baf2fd6bc196f7a727a26-5be5bc7b00d3e118-00"
}`,
  table: respTable([
    ["errors", "Object", "A dictionary of validation errors. Keys are field names, and values are Arrays of messages."],
    ["type", "String", "An HTTP URI reference indicating the error type."],
    ["title", "String", "A short summary of the issue – typically \"One or more validation errors occurred.\""],
    ["status", "Integer", "HTTP status code – here, 400 (Bad Request)."],
    ["traceId", "String", "Unique identifier for the request – useful for tracing errors in logs."],
  ]),
};

const riskProfileBodyBlocks = [
  { heading: "Mandatory Policies (Pre Trade Policies)", table: mandatoryPoliciesTable },
  { zone: "rightcode", heading: "Example", code: mandatoryPoliciesJson },
  {
    heading: "Column Descriptions",
    list: [
      "ID: Name or identifier of the policy.",
      "Type: Numeric code indicating the policy type.",
      "Config: Configuration Array (cfg) – additional settings (empty in this case).",
      { label: "Action Type: The supported actions are:", subItems: ["0 → Reject the order (default)", "1 → Generate warning"] },
    ],
  },
  { zone: "rightcode", heading: "Required Body", code: requiredBodyJson },
  { zone: "rightcode", heading: "Request Body Example", code: createRiskProfileExampleJson },
  { heading: "Request Body Fields", table: { columns: fieldColumns, rows: riskProfileBodyRows } },
  { heading: "Pre Trade Policies Table", table: preTradeStructTable },
  { heading: "Post Trade Policies Table", table: postTradeStructTable },
  { heading: "Example of cfg inside pre-trade policy[]:", table: cfgPreTradeExampleTable },
  { heading: "Example of cfg inside post-trade policy[]:", table: cfgPostTradeExampleTable },
  { text: "Please refer to the end of this document for detailed information on pre-trade and post-trade policies." },
];

// ---------------------------------------------------------------------------
// Update Risk Profile content blocks
// ---------------------------------------------------------------------------
const updateRiskProfileExampleJson = `{
  "id": "Regress2",
  "name": "Regress2",
  "pre_trade_policies": [
    { "id": "Suspended", "type": 4, "cfg": [], "action": { "type": 0 } },
    { "id": "Liquidation Only", "type": 3, "cfg": [], "action": { "type": 0 } },
    {
      "id": "Options Zero DTE Sell Short Order QTY Limit #",
      "type": 38,
      "cfg": [{ "key": "threshold", "val": "50" }],
      "action": { "type": 0 }
    },
    {
      "id": "Restricted Symbol",
      "type": 33,
      "cfg": [{ "key": "blacklist", "val": "SSSS" }],
      "action": { "type": 0 }
    },
    {
      "id": "Order QTY Limit #",
      "type": 22,
      "cfg": [{ "key": "threshold", "val": "50" }],
      "action": { "type": 0 }
    }
  ],
  "post_trade_policies": [
    {
      "id": "Max Position Size per Symbol",
      "type": 5,
      "layers": [
        {
          "action": { "type": 2 },
          "cfg": [{ "key": "threshold", "val": "50" }]
        }
      ]
    }
  ]
}`;

const updateRiskProfileRequiredBodyJson = `{
  "id": "ABC1234567",
  "name": "ABC1234567",
  "pre_trade_policies": [
    { "id": "Suspended", "type": 4, "cfg": [], "action": { "type": 0 } },
    { "id": "Liquidation Only", "type": 3, "cfg": [], "action": { "type": 0 } }
  ],
  "post_trade_policies": []
}`;

const updateRiskProfileBodyFieldRows = [
  ["id", "Yes", "String", "Unique ID risk profile."],
  ["name", "Yes", "String", "Name of the risk profile."],
  ["pre_trade_policies[]", "Yes", "Array", "List of rules/policies to be enforced before trade execution."],
  ["id (pre_trade)", "Yes", "String", "Unique name for the policy group."],
  ["type (pre_trade)", "Yes", "Integer", "Type code of the pre-trade policy."],
  ["cfg (pre_trade)", "Yes", "Array", "Configuration key-value pairs (used when the rule is simple and does not require layering)."],
  ["action (pre_trade)", "Yes", "Object", "Defines what action to take when the condition is met (0 = reject, 1 = warning)."],
  ["POST_trade_policies", "No", "Array", "List of rules/policies enforced after trades. Always structured with layers."],
  ["layers[] (post_trade)", "Yes", "Object", "Required for (POST-trade policy)."],
  ["layers[].id (post_trade)", "Yes", "String", "Unique name for the policy group."],
  ["layers[].type (post_trade)", "Yes", "Integer", "Numeric type code for the policy."],
  ["layers[].cfg (post_trade)", "Yes", "Object", "Array of key-value config settings for the policy."],
  ["layers[].action", "Yes", "Object", "Defines action to take when policy is triggered: 0 → reject (default), 1 → warning."],
].map(([field, required, type, desc], i) => ({
  num: cellText(String(i + 1)),
  fieldName: required === "Yes" ? cellRequired(field) : cellText(field),
  paramType: cellCaption(required),
  dataType: badge(type),
  description: cellCaption(desc),
}));

const updateRiskProfileBodyBlocks = [
  {
    zone: "lead",
    heading: "Request Parameters",
    table: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("riskProfileId"), description: cellCaption("Unique ID of the riskProfileId.") },
      ],
    },
  },
  { heading: "Mandatory Policies (Pre Trade Policies)", table: mandatoryPoliciesTable },
  { zone: "rightcode", heading: "Example", code: mandatoryPoliciesJson },
  {
    heading: "Column Descriptions",
    list: [
      "ID: Name or identifier of the policy.",
      "Type: Numeric code indicating the policy type.",
      "Config: Configuration Array (cfg) – additional settings (empty in this case).",
      { label: "Action Type: The supported actions are:", subItems: ["0 → Reject the order (default)", "1 → Generate warning"] },
    ],
  },
  { zone: "rightcode", heading: "Required Body", code: updateRiskProfileRequiredBodyJson },
  { zone: "rightcode", heading: "Request Body Example", code: updateRiskProfileExampleJson },
  { heading: "Request Body Fields", table: { columns: fieldColumns, rows: updateRiskProfileBodyFieldRows } },
  { heading: "Pre Trade Policies Table", table: preTradeStructTable },
  { heading: "Post Trade Policies Table", table: postTradeStructTable },
  { heading: "Example of cfg inside pre-trade policy[]:", table: cfgPreTradeExampleTable },
  { heading: "Example of cfg inside post-trade policy[]:", table: cfgPostTradeExampleTable },
  { text: "Please refer to the end of this document for detailed information on pre-trade and post-trade policies." },
];

// Update Risk Profile 200-success response field rows (21 rows, matches doc).
const updateRiskProfileRespRows = [
  ["id", "String", "Unique ID for the policy group."],
  ["name", "String", "Name of the policy group (id)."],
  ["pre_trade_policies", "Array", "List of policies enforced before trade execution."],
  ["id (pre-trade)", "String", "Unique ID of the pre-trade policy."],
  ["type (pre-trade)", "Integer", "Type code of the pre-trade policy."],
  ["cfg (pre-trade)", "Array", "Configuration key-value pairs (for simple rule definitions)."],
  ["action (pre-trade)", "Object", "Defines action to take if condition is met (0 → reject, 1 → warning)."],
  ["POST_trade_policies", "Array", "List of policies enforced after trades. Always includes layers."],
  ["id (post-trade)", "String", "Unique ID of the POST-trade policy."],
  ["type (post-trade)", "Integer", "Type code of the POST-trade policy."],
  ["cfg (post-trade)", "Object", "Config Object containing thresholds or parameters per layer."],
  ["layers", "Object", "When policies have complex logic, defines an Array of conditional configs with actions."],
  ["layers[].action", "Object", "Enforcement action for each layer. (0 → reject, 1 → warning)."],
  ["used_by", "Object", "Object with lists of firms, groups, and traders associated with the policy."],
  ["firms", "String[]", "List of firm IDs."],
  ["groups", "String[]", "List of group IDs."],
  ["traders", "String[]", "List of trader IDs."],
  ["assigned_firm", "Integer", "Firm ID assigned to this policy group (0 if none)."],
  ["assigned_group", "Integer", "Group ID assigned to this policy group (0 if none)."],
  ["assigned_account", "Integer", "Account ID assigned to this policy group (0 if none)."],
  ["createdDate", "DateTime", "Timestamp when created."],
];

const updateRiskProfile400Table = {
  status: "Status Code: 400 (Bad Request)",
  statusClass: "code-red",
  code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Risk profile POSPLProfile91 already exists.",
  "Instance": "/risk_profiles",
  "Extensions": {}
}`,
  table: respTable([
    ["type", "String", "A URI reference that identifies the problem type (e.g., \"about:blank\" for generic issues)."],
    ["title", "String", "A short, human-readable summary of the problem — typically indicates a validation failure."],
    ["status", "Integer", "HTTP status code — e.g., 400 for Bad Request."],
    ["detail", "String", "A specific explanation of the issue — e.g., \"profile already exists.\""],
    ["instance", "String", "The request path where the error occurred (e.g., /risk_profiles)."],
    ["extensions", "Object", "Optional additional data (empty or containing extra context)."],
  ]),
};

const marginProfileRespRows = [
  ["used_by.firms/groups/traders", "Object", "List of IDs where this profile is currently used."],
  ["name", "String", "Name of the profile."],
  ["id", "String", "Unique identifier."],
  ["symbol_overrides", "Array", "List symbol overrides."],
  ["maint_options_house_min_rate", "Float", "Minimum house maintenance % for any option."],
  ["maint_index_options_house_short_rate", "Float", "Maintenance % for short index options (house)."],
  ["maint_stock_options_house_short_rate", "Float", "Maintenance % for short stock options (house)."],
  ["maint_stocks_house_short_rate", "Float", "Maintenance % for short stocks (house)."],
  ["maint_stocks_house_Long_rate", "Float", "Maintenance % for Long stocks (house)."],
  ["initial_stocks_day_min_requirement", "Float", "Minimum initial % for day trading stocks."],
  ["initial_stocks_regt_ovn_rate", "Float", "Initial margin % for overnight stocks."],
  ["initial_stocks_regt_day_rate", "Float", "Initial margin % for day stocks."],
  ["initial_stock_options_regt_short_rate", "Float", "Initial margin % for short stock options."],
  ["initial_index_options_regt_short_rate", "Float", "Initial margin % for short index options."],
  ["initial_options_regt_min_rate", "Float", "Initial margin minimum rate for options."],
  ["maint_stocks_regt_Long_rate", "Float", "Maintenance % for Long stocks."],
  ["maint_stocks_regt_short_rate", "Float", "Maintenance % for short stocks."],
  ["maint_stock_under_2_5_regt_short_per_share", "Float", "Per-share margin if price is under $2.50."],
  ["maint_stock_under_5_0_regt_short_rate", "Float", "Maintenance % if stock is under $5.00."],
  ["maint_stock_under_X_regt_short_per_share", "Float", "Per-share rule for custom threshold \"X\"."],
  ["maint_stock_options_regt_short_rate", "Float", "Maintenance % for short stock options."],
  ["maint_index_options_regt_short_rate", "Float", "Maintenance % for short index options."],
  ["maint_options_regt_min_rate", "Float", "Maintenance minimum rate for any option."],
];

// Full margin profile request field list [name, required, description].
const marginFieldData = [
  ["id", "Yes", "Id of margin Profile"],
  ["symbol_overrides", "No", "List symbol overrides."],
  ["maint_options_house_min_rate", "No", "Minimum house maintenance % for any option."],
  ["maint_index_options_house_short_rate", "No", "Maintenance % for short index options (house)."],
  ["maint_stock_options_house_short_rate", "No", "Maintenance % for short stock options (house)."],
  ["maint_stocks_house_short_rate", "No", "Maintenance % for short stocks (house)."],
  ["maint_stocks_house_Long_rate", "No", "Maintenance % for Long stocks (house)."],
  ["initial_stocks_day_min_requirement", "Yes", "Minimum initial % for day trading stocks."],
  ["initial_stocks_regt_ovn_rate", "No", "Initial margin % for overnight stocks."],
  ["initial_stocks_regt_day_rate", "No", "Initial margin % for day stocks."],
  ["initial_stock_options_regt_short_rate", "No", "Initial margin % for short stock options."],
  ["initial_index_options_regt_short_rate", "No", "Initial margin % for short index options."],
  ["initial_options_regt_min_rate", "No", "Initial margin minimum rate for options."],
  ["maint_stocks_regt_Long_rate", "No", "Maintenance % for Long stocks."],
  ["maint_stocks_regt_short_rate", "No", "Maintenance % for short stocks."],
  ["maint_stock_under_2_5_regt_short_per_share", "No", "Per-share margin if price is under $2.50."],
  ["maint_stock_under_5_0_regt_short_rate", "No", "Maintenance % if stock is under $5.00."],
  ["maint_stock_under_X_regt_short_per_share", "No", "Per-share rule for custom threshold \"X\"."],
  ["maint_stock_options_regt_short_rate", "No", "Maintenance % for short stock options."],
  ["maint_index_options_regt_short_rate", "No", "Maintenance % for short index options."],
  ["maint_options_regt_min_rate", "No", "Maintenance minimum rate for any option."],
];

// Build request-field rows from [name, required, description] tuples.
const marginFieldRows = (data) =>
  data.map(([field, required, desc], i) => ({
    num: cellText(String(i + 1)),
    fieldName: required === "Yes" ? cellRequired(field) : cellText(field),
    paramType: cellCaption(required),
    dataType: badge(field === "symbol_overrides" ? "Array" : field === "id" || field === "name" ? "String" : "Float"),
    description: cellCaption(desc),
  }));

const firmRespRows = [
  ["id", "Long", "Numeric firm ID."],
  ["name", "String", "Firm name."],
  ["trading_mode", "Integer", "Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively."],
  ["risk_profile", "String", "Firm risk profile."],
  ["default_group_risk_profile", "String", "Risk profile assigned to new groups."],
  ["default_trader_risk_profile", "String", "Risk profile assigned to new trader."],
  ["margin_profile", "String", "Margin profile."],
  ["include_pnl_in_excess", "Integer", "Include_pnl_in_excess value.Possible value 0,1,2 - do not include pnl, include pnl, include loss only respectively"],
  ["created_time", "DateTime", "Created Time"],
  ["updated_time", "DateTime", "Updated Time"],
];

const traderGroupRespRows = [
  ["id", "Integer", "Numeric ID for the trader group."],
  ["trader_group_id", "String", "Unique identifier of the trader group."],
  ["name", "String", "Name of the trader group."],
  ["firm_id", "String", "Firm ID associated with the group."],
  ["trading_mode", "Integer", "0, 1, 2 \u2013 unrestricted, liquidation only, suspended respectively."],
  ["risk_profile", "String", "Group-level risk profile."],
  ["default_trader_risk_profile", "String", "Default risk profile assigned to traders in this group."],
  ["has_equity_allocation", "Boolean", "Whether the group has equity allocation."],
  ["account_type", "Integer", "Account type."],
  ["ovn_trading_multiple", "Float", "Overnight trading multiple."],
  ["day_trading_multiple", "Float", "Day trading multiple."],
  ["createdTime", "DateTime", "Date the group was created."],
  ["updatedTime", "DateTime", "Timestamp when last updated."],
];

// Trader group request/response fields table rows (numbered 1-12).
const traderGroupFieldRows = [
  { num: cellText("1"), fieldName: cellRequired("trader_group_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique identifier for the trader group.") },
  { num: cellText("2"), fieldName: cellText("name"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Name of the trader group.") },
  { num: cellText("3"), fieldName: cellRequired("firm_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Firm under which this group is created.") },
  { num: cellText("4"), fieldName: cellText("trading_mode"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("0 = unrestricted, 1 = liquidation only, 2 = suspended.") },
  { num: cellText("5"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Group-level risk profile.") },
  { num: cellText("6"), fieldName: cellText("default_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Default risk profile.") },
  { num: cellText("7"), fieldName: cellText("has_equity_allocation"), paramType: cellCaption("No"), dataType: badge("Boolean"), description: cellCaption("Has equity allocation.") },
  { num: cellText("8"), fieldName: cellText("account_type"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Account Type.") },
  { num: cellText("9"), fieldName: cellText("ovn_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("Ovn trading multiple.") },
  { num: cellText("10"), fieldName: cellText("day_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("Day Trading multiple.") },
  { num: cellText("11"), fieldName: cellText("createdTime"), paramType: cellCaption("No"), dataType: badge("DateTime"), description: cellCaption("Created Time") },
  { num: cellText("12"), fieldName: cellText("updatedTime"), paramType: cellCaption("No"), dataType: badge("DateTime"), description: cellCaption("Updated Date Time.") },
];

const traderRespRows = [
  ["id", "Integer", "Numeric unique ID of the trader"],
  ["trader_id", "String", "Trader's unique identifier"],
  ["name", "String", "Name of the trader (trader_id)"],
  ["firm_id", "String", "ID of the firm the trader belongs to"],
  ["group_id", "String", "Group ID the trader is assigned to"],
  ["short_vs_box", "Boolean", "Short vs box restriction flag"],
  ["account_type", "Integer", "Following values are supported (0,1,2,3,4)"],
  ["pdt", "Boolean", "Indicates if the trader is marked as a Pattern Day Trader (PDT)."],
  ["ovn_trading_multiple", "Float", "O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0], default 2.0."],
  ["day_trading_multiple", "Float", "Day trading multiple. Following values are supported must be in the interval [1.0, 4.0],but default is 4.0."],
  ["trading_mode", "Integer", "Possible value 0, 1, 2 \u2013 unrestricted, liquidation only, suspended respectively."],
  ["risk_profile", "String", "Assigned risk profile ID."],
  ["created_time", "Long", "Created time."],
  ["updated_time", "Long", "Updated time."],
];

// Combined data[] trader table (top-level + nested) for GET All Trader variants.
const traderDataTableRows = respRows([
  ["data", "Array", "Array of trader Objects. See table below for fields inside each trader."],
  ["totalCount", "Integer", "Total number of traders (pagination support; may be -1 if not used)."],
  ["groupCount", "Integer", "Number of trader groups (optional; -1 if not used)."],
]);

const traderObjectFieldRows = respRows([
  ["id", "Integer", "Unique Integer Id."],
  ["trader_id", "String", "Unique identifier of the trader."],
  ["name", "String", "Name of the trader."],
  ["firm_id", "String", "Identifier of the firm."],
  ["group_id", "String", "Group to which the trader is assigned."],
  ["short_vs_box", "Boolean", "Indicates whether short vs box is allowed."],
  ["account_type", "Integer", "Following values are supported (0,1,2,3,4)."],
  ["pdt", "Boolean", "Indicates if the trader is marked as a Pattern Day Trader (PDT)."],
  ["ovn_trading_multiple", "Float", "O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0."],
  ["day_trading_multiple", "Float", "day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0."],
  ["trading_mode", "Integer", "Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively."],
  ["risk_profile", "String", "Assigned risk profile ID."],
  ["created_time", "Long", "Created Time."],
  ["updated_time", "Long", "Updated Time."],
]);

// 12-field trader response table (Create / Bulk / CSV 200 response).
const traderResponse200Table = {
  columns: fieldColumns,
  rows: [
    { num: cellText("1"), fieldName: cellText("firm_id"), dataType: badge("String"), description: cellCaption("Identifier of the firm to which the trader belongs") },
    { num: cellText("2"), fieldName: cellText("trader_id"), dataType: badge("String"), description: cellCaption('Unique identifier for the trader (e.g., "PK_0014").') },
    { num: cellText("3"), fieldName: cellText("name"), dataType: badge("String"), description: cellCaption("Name of the trader (trader_id).") },
    { num: cellText("4"), fieldName: cellText("short_vs_box"), dataType: badge("Boolean"), description: cellCaption("Indicates if short vs. box strategy is enabled (true or false).") },
    { num: cellText("5"), fieldName: cellText("account_type"), dataType: badge("Integer"), description: cellCaption("Type of trading account") },
    { num: cellText("6"), fieldName: cellText("pdt"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
    { num: cellText("7"), fieldName: cellText("ovn_trading_multiple"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
    { num: cellText("8"), fieldName: cellText("day_trading_multiple"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
    { num: cellText("9"), fieldName: cellText("trading_mode"), dataType: badge("Integer"), description: cellCaption("0,1,2 - restricted") },
    { num: cellText("10"), fieldName: cellText("risk_profile"), dataType: badge("String"), description: cellCaption('Assigned risk profile name (e.g., "IALI").') },
    { num: cellText("11"), fieldName: cellText("created_time"), dataType: badge("Long"), description: cellCaption("Created Time") },
    { num: cellText("12"), fieldName: cellText("updated_time"), dataType: badge("Long"), description: cellCaption("Updated Time") },
  ],
};

// Trader request fields table (Create / Bulk / CSV) — verbatim per spec numbering.
const traderRequestFieldRows = [
  { num: cellText("1"), fieldName: cellRequired("trader_group_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique identifier for the trader group.") },
  { num: cellText("3"), fieldName: cellRequired("firm_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Firm under which this group is created.") },
  { num: cellText("4"), fieldName: cellText("trading_mode"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
  { num: cellText("5"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Group-level risk profile.") },
  { num: cellText("6"), fieldName: cellText("default_trader_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Risk profile automatically applied to any new trader in this group.") },
  { num: cellText("7"), fieldName: cellText("short_vs_box"), paramType: cellCaption("True"), dataType: badge("Boolean"), description: cellCaption("") },
  { num: cellText("8"), fieldName: cellText("account_type"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Following values are supported (0,1,2,3,4)") },
  { num: cellText("9"), fieldName: cellText("ovn_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
  { num: cellText("10"), fieldName: cellText("day_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
  { num: cellText("11"), fieldName: cellText("pdt"), paramType: cellCaption("false"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
];

// ---------------------------------------------------------------------------
// Pre / Post trade policies tables
// ---------------------------------------------------------------------------
const preTradePoliciesData = [
  ["Min ADV Required", "Reject the order if the stock's Average Daily Volume (ADV), retrieved from a data store, is less than a specified numeric limit (e.g., 100,000).", "name: min_adv | type: 5 | cfg: threshold = min ADV (Integer [0, ∞))"],
  ["Opening Order QTY ADV %", "This rule applies to opening orders, reject the order if the order quantity exceeds a specified percentage (e.g., 20%) of the stock's Average Daily Volume (ADV), where ADV is retrieved from a data store.", "name: max_opening_order_qty_adv_percentage | type: 6 | cfg: adv% = adv percentage (Float [0, 100])"],
  ["Closing Order QTY ADV %", "Same as above but applied exclusively to closing orders.", "name: max_closing_order_qty_adv_percentage | type: 7 | cfg: adv% = adv percentage (Float [0, 100])"],
  ["MKT Opening Order QTY % ADV", "Applies to market opening orders; rejects if quantity surpasses a defined percentage of ADV.", "name: max_market_opening_order_qty_adv_percentage | type: 8 | cfg: adv% = adv percentage (Float [0, 100])"],
  ["MKT Closing Order QTY % ADV", "Applies to market closing orders with the same ADV percentage restriction.", "name: max_market_closing_order_qty_adv_percentage | type: 9 | cfg: adv% = adv percentage (Float [0, 100])"],
  ["Total Trades % of ADV", "For stocks, calculates net traded quantity as the absolute difference between total buys and sells for the day. Reject if it exceeds a specified ADV percentage.", "name: max_net_symbol_trades_adv_percentage | type: 10 | cfg: adv% = percentage of ADV (Float)"],
  ["Open Orders % of ADV", "For each symbol, calculates the larger of total buy or sell open order quantities. Reject if this exceeds a specified percentage of ADV, preventing excessive open interest.", "name: max_net_open_orders_qty_adv_percentage | type: 11 | cfg: adv% = adv percentage (Float)"],
  ["Total Trades Shares Limit", "Sets a maximum gross share volume for all trades during the day; rejects new orders if exceeded.", "name: max_gross_symbol_trades_absolute | type: 12 | cfg: threshold = shares limit (Integer [0, ∞))"],
  ["Open Order Shares Limit", "Limits the total open order shares (buy and sell combined); rejects new orders if limit is exceeded.", "name: max_gross_open_orders_qty | type: 13 | cfg: threshold = open‑order shares limit (Integer [0, ∞))"],
  ["Trades + Open Orders Shares Limit", "Caps the combined total shares traded plus shares in open orders; rejects any new order exceeding this cap.", "name: max_total_trades_and_open_orders_quantity | type: 14 | cfg: threshold = shares limit (Integer [0, ∞))"],
  ["Open Order Count Limit", "Limits the maximum number of open orders per stock; rejects new orders if the count surpasses this limit.", "name: max_gross_open_orders_count | type: 15 | cfg: threshold = open-orders count limit (Integer [0, ∞))"],
  ["Open Orders Count Limit per Security", "Caps the total number of opening buy and sell orders per symbol; rejects new opening orders if the limit is exceeded (closing orders excluded).", "name: max_gross_symbol_open_orders_count | type: 16 | cfg: threshold = absolute value (Integer [0, ∞))"],
  ["Max Bid/Ask Layers for Open Orders", "Limits the maximum number of buy or sell open orders per symbol; rejects new orders on the side exceeding the limit.", "name: max_buy_sell_symbol_open_orders_count | type: 17 | cfg: threshold = max order count (Integer [0, ∞))"],
  ["Max Share Price", "Rejects any limit order with a price above the specified maximum.", "name: max_share_price | type: 18 | cfg: threshold = max share price (Float)"],
  ["Min Share Price", "Rejects any limit order with a price below the specified minimum.", "name: min_share_price | type: 19 | cfg: threshold = min share price (Float [0, ∞))"],
  ["Max Order Price Deviation %", "For limit and stop-limit orders, rejects if the absolute price deviation from last traded price exceeds a set percentage.", "name: max_order_price_deviation | type: 20 | cfg: deviation% = percentage limit (Float [0, ∞))"],
  ["Order Notional Limit", "For stocks, rejects opening orders whose notional value (quantity × relevant price) exceeds a dollar limit. Different order types use respective price references; order quantity is treated as absolute value.", "name: max_opening_order_notional_value | type: 21 | cfg: threshold = max order value (Float [0, ∞))"],
  ["Order QTY Limit", "Rejects opening orders exceeding a specified share quantity limit.", "name: max_opening_order_qty | type: 22 | cfg: threshold = max order qty (Integer [0, ∞))"],
  ["Min Cancel Time", "Sets a minimum interval (in milliseconds) between order placement and cancellation; cancels requested before this period are rejected.", "name: min_cancel_time | type: 23 | cfg: threshold = min interval ms (Integer [0, ∞))"],
  ["MOC Order Notional Limit", "For Market-On-Close orders, rejects those exceeding a notional dollar value limit.", "name: max_opening_moc_order_notional_value | type: 24 | cfg: threshold = max notional value (Float)"],
  ["MOC Order Qty Limit", "For Market-On-Close orders, rejects those exceeding a share quantity limit.", "name: max_opening_moc_order_qty | type: 25 | cfg: threshold = max qty (Integer [0, ∞))"],
  ["MOO Order Notional Limit", "For Market-On-Open orders, rejects those exceeding a notional dollar value limit.", "name: max_opening_moo_order_notional_value | type: 34 | cfg: threshold = max notional value (Float [0, ∞))"],
  ["MOO Order Qty Limit", "For Market-On-Open orders, rejects those exceeding a share quantity limit.", "name: max_opening_moo_order_qty | type: 35 | cfg: threshold = max qty (Integer [0, ∞))"],
  ["Total Combined Margin Limit", "For RegT or PM accounts, rejects pattern day trade orders exceeding day trading margin excess or non-pattern trades exceeding overnight margin excess.", "name: max_total_overall_margin_requirement | type: 26"],
  ["Options Buy Order Qty Limit", "Sets max quantity for opening options buy positions with separate limits for Long and short orders. Overrides only reinforce limits. Must comply with global and override limits.", "name: max_options_long_order_qty | type: 27 | cfg: threshold = max qty (Integer)"],
  ["Options Sell-Short Order Qty Limit", "Sets max quantity for opening options sell-short positions with distinct limits for Long (buy) and short (sell short) orders. Overrides only tighten limits. Compliance required.", "name: max_options_short_order_qty | type: 36 | cfg: threshold = max qty (Integer)"],
  ["Options Zero-DTE Buy Qty Limit", "Limits quantity on options buy orders expiring the same trading day (zero days-to-expiry).", "name: max_options_zero_dte_long_order_qty | type: 37 | cfg: threshold = max qty (Integer)"],
  ["Options Zero-DTE Sell-Short Qty Limit", "Limits quantity on options sell-short orders expiring the same trading day.", "name: max_options_zero_dte_short_order_qty | type: 38 | cfg: threshold = max qty (Integer)"],
  ["Options Symbols Buy Qty Limit", "Monitors buy orders exceeding set quantity thresholds for specific option symbols to control large buys.", "name: max_options_symbols_long_order_qty | type: 39 | cfg: symbols = …; threshold = max qty"],
  ["Options Symbols Sell-Short Qty Limit", "Flags and restricts sell-short orders exceeding set quantity thresholds on specific option symbols.", "name: max_options_symbols_short_order_qty | type: 40 | cfg: symbols = …; threshold = max qty"],
  ["Options Order Price Check %", "Ensures limit/stop-limit order prices are within a specified percentage of the option's last traded price. Rejects orders outside this range; applies to single-leg orders only.", "name: max_options_order_price_deviation | type: 28 | cfg: deviation% = percentage limit"],
  ["Min Options Order Price", "Validates that option order price exceeds a specified minimum threshold.", "name: min_options_share_price | type: 29 | cfg: threshold = min share price (Float)"],
  ["Max Options Order Price", "Ensures option order price does not exceed a specified maximum threshold.", "name: max_options_share_price | type: 30 | cfg: threshold = max share price (Float)"],
  ["Max Options Open Orders Count", "Limits total open buy and sell option orders for a symbol. New orders rejected if combined count exceeds the limit.", "name: max_options_gross_open_orders_count | type: 31 | cfg: threshold = open-orders count limit (Integer)"],
  ["Min Options Order Cancel Time", "Rejects cancellation requests made too soon after order placement, based on a millisecond threshold.", "name: min_options_order_cancel_time | type: 32 | cfg: threshold = min interval ms (Integer)"],
  ["Restricted Symbols", "This feature allows the Risk Manager to block new order submissions for specific symbols without canceling existing open orders. Restrictions can be explicitly lifted to resume normal trading for the affected symbol.", "name: symbols_blacklist | type: 33 | cfg: blacklist = semicolon-separated symbol list"],
  ["Restricted Options Contracts", "This feature allows the Risk Manager to blacklist specific options contracts, preventing any new order submissions for those contracts. Existing open orders remain unaffected, and the blacklist can be updated to allow trading again when needed.", "name: options_contracts_blacklist | type: 41 | cfg: blacklist = semicolon-separated OSI list"],
  ["Max Equity Order Duplicates / Timeframe", "Limits duplicate orders (same account, symbol, order type, side, qty, price) allowed within a configurable time window; rejects further duplicates beyond the maximum.", "name: max_equity_order_duplicates | type: 42 | cfg: timeframe (ms); allowed duplicates"],
  ["Max Options Order Duplicates / Timeframe", "Limits duplicate option orders from the same account within a defined timeframe, matching account, symbol, type, side, quantity, and price.", "name: max_option_order_duplicates | type: 43 | cfg: timeframe (ms); allowed duplicates"],
  ["Equities Aggressive Price Check", "Defines aggressive trades as buys priced at/above NBO and sells at/below NBB, allowing a configurable deviation calculated as the max between a percentage and fixed dollar amount.", "name: equities_aggressive_price_check | type: 44 | cfg: delta% / min_delta"],
  ["Options Aggressive Price Check", "Flags aggressive trades where buy orders price ≥ National Best Offer (NBO) and sell orders price ≤ National Best Bid (NBB), with configurable deviation for low-priced stocks.", "name: options_aggressive_price_check | type: 45 | cfg: delta% / min_delta"],
  ["Equities Passive Price Check", "Defines passive trades as buys below NBB and sells above NBO, applying a similar configurable deviation for price tolerance.", "name: equities_passive_price_check | type: 46 | cfg: delta% / min_delta"],
  ["Options Passive Price Check", "Flags passive trades where buy orders price < National Best Bid (NBB) and sell orders price > National Best Offer (NBO), with configurable deviation for low-priced stocks.", "name: options_passive_price_check | type: 47 | cfg: delta% / min_delta"],
  ["Gross Notional Cumulative Limit", "Sums the notional values of all buy and sell open orders and executed trades; rejects new orders if the total exceeds a threshold.", "name: max_gross_cumulative_notional_value | type: 48 | cfg: threshold = absolute $ limit"],
  ["Net Notional Cumulative Limit", "Calculates the absolute net difference between buy-side and sell-side notionals (orders plus trades); rejects orders exceeding a threshold.", "name: max_net_cumulative_notional_value | type: 49 | cfg: threshold = absolute $ limit"],
  ["Trading Halted", "Blocks all trading on symbols flagged as halted via market data; rejects any orders on halted symbols until trading resumes.", "name: trading_halted | type: 50 | cfg: allow_limit_orders = Boolean"],
  ["Min Price Increment", "For equities priced above $1, enforces a minimum price increment of $0.10 (one Float place). Prices below $1 may have finer increments; prices violating this precision are rejected.", "name: equities_min_price_increment | type: 51"],
];

const preTradePolicyRows = preTradePoliciesData.map(([policy, def, cfg], i) => ({
  num: cellText(String(i + 1)),
  fieldName: cellText(policy),
  paramType: cellCaption(def),
  description: cellCaption(cfg),
}));

const postTradePoliciesData = [
  ["Loss Limit $", "Monitors combined PnL loss; blocks trading if loss exceeds fixed dollar threshold.", "name: max_total_loss_limit_absolute | type: 1 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Loss Limit %", "Caps losses as a percentage of trader's equity; blocks trading if exceeded.", "name: max_total_loss_limit_equity_percentage | type: 2"],
  ["Loss Limit Sym $", "Limits losses on a specific symbol by fixed dollar amount; blocks trading if exceeded.", "name: max_symbol_loss_limit_absolute | type: 3 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Loss Limit Sym %", "Limits losses on a symbol as a percentage of equity; blocks trading if exceeded.", "name: max_symbol_loss_limit_equity_percentage | type: 4 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Max Position Size per Symbol", "Limits max shares per symbol; blocks trading if position size exceeded.", "name: max_symbol_position_limit_absolute | type: 5 | cfg: threshold = absolute position limit (Integer [0, \u221e))"],
  ["Max Position Size per Symbol ADV%", "Limits position size per symbol based on average daily volume percentage; blocks if exceeded.", "name: max_symbol_position_limit_adv_percentage | type: 6 | cfg: adv% = adv percentage (Float [0.0, 100.0])"],
  ["Position Exposure Limit $ per Symbol", "Limits position value per symbol; triggers warnings or blocks if exceeded.", "name: max_symbol_position_exposure_absolute | type: 7 | cfg: threshold = absolute limit (Float [0, \u221e))"],
  ["Position Exposure Limit % of Equity", "Caps position value per symbol as percentage of equity; restricts trades if exceeded.", "name: max_symbol_position_exposure_equity_percentage | type: 8 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Max L/S position exposure $", "Limits max long or short position exposure; blocks trades if exceeded.", "name: max_long_short_position_exposure_absolute | type: 9 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Max L/S position exposure %", "Caps combined long/short exposure as percentage of equity; blocks trades if exceeded.", "name: max_long_short_position_exposure_equity_percentage | type: 10 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Total position exposure $", "Limits total value of all long and short positions; blocks trades if exceeded.", "name: max_gross_position_exposure_absolute | type: 11 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Total position exposure %", "Limits total position exposure as percentage of equity; blocks trades if exceeded.", "name: max_gross_position_exposure_equity_percentage | type: 12 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Max L/S orders exposure $", "Caps total buy or sell order exposure as percentage of equity; blocks orders if exceeded.", "name: max_long_short_orders_exposure_absolute | type: 13 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Max L/S orders exposure %", "Limits dollar value of outstanding buy or sell orders per side; blocks if exceeded.", "name: max_long_short_orders_exposure_equity_percentage | type: 14 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Total orders exposure $", "Limits combined value of all open buy and sell orders; blocks if exceeded.", "name: max_gross_orders_exposure_absolute | type: 15 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Total orders exposure %", "Caps combined open orders based on equity percentage; blocks if exceeded.", "name: max_gross_orders_exposure_equity_percentage | type: 16 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Max L/S combined exposure $", "Caps total exposure from orders + positions on one side (long/short); blocks if exceeded.", "name: max_long_short_combined_exposure_absolute | type: 17 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Max L/S combined exposure %", "Limits combined exposure on one side as percentage of equity; blocks trades if exceeded.", "name: max_long_short_combined_exposure_equity_percentage | type: 18 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
  ["Total combined exposure $", "Limits total exposure from all orders and positions combined; blocks if exceeded.", "name: max_gross_combined_exposure_absolute | type: 19 | cfg: threshold = absolute $ limit (Float [0, \u221e))"],
  ["Total combined exposure %", "Caps total combined exposure as percentage of equity; blocks if exceeded.", "name: max_gross_combined_exposure_equity_percentage | type: 20 | cfg: equity% = equity percentage (Float [0.0, 666.0])"],
];

const postTradePolicyRows = postTradePoliciesData.map(([policy, def, cfg], i) => ({
  num: cellText(String(i + 1)),
  fieldName: cellText(policy),
  paramType: cellCaption(def),
  description: cellCaption(cfg),
}));

const policyTableColumns = [
  { Header: "#", accessor: "num", align: "left", width: "5%" },
  { Header: "Policy", accessor: "fieldName", align: "left", width: "20%" },
  { Header: "Definition", accessor: "paramType", align: "left", width: "45%" },
  { Header: "Description", accessor: "description", align: "left" },
];

const queryParamData = [
  ["requireTotalCount", "If true, returns the total count of records."],
  ["requireGroupCount", "If true, returns the total count of groups."],
  ["isCountQuery", "If true, only the count will be returned."],
  ["isSummaryQuery", "If true, only summary values are returned."],
  ["skip / take", "Skip/take used for paging (skip = offset, take = limit)."],
  ["sort", "JSON Array of sorting options."],
  ["group", "JSON Array of grouping settings."],
  ["filter", "JSON Array or String filter expression."],
  ["totalSummary", "JSON Array to define total summary (e.g., sum, avg) of certain fields."],
  ["groupSummary", "Summary type per group."],
  ["select", "Select only certain fields."],
  ["preSelect", "Fields to pre-select before applying main selection."],
  ["remoteSelect", "Enable remote selection at the database level."],
  ["remoteGrouping", "Enable remote grouping in the database."],
  ["expandLinqSumType", "Expands sum types in LINQ queries."],
  ["primaryKey", "Used for pagination using the primary key."],
  ["defaultSort", "Default sort field when none is provided."],
  ["stringToLower", "If true, all String comparisons are case-insensitive."],
  ["paginateViaPrimaryKey", "Use key-based pagination."],
  ["sortByPrimaryKey", "Ensures sorting includes primary key to avoid skipping records."],
  ["allowAsyncOverSync", "Allows async behavior even if original method is synchronous."],
];

const queryParamRows = queryParamData.map(([name, desc]) => ({
  fieldName: cellText(name),
  description: cellCaption(desc),
}));

// ---------------------------------------------------------------------------
// Documentation data
// ---------------------------------------------------------------------------
const griskApiData = [
  {
    mainHeadingStatic: "Introduction",
    mainGridIdStatic: "introduction",
    mainHeadingDescStatic:
      "Risk & Margin API Software is a robust policy enforcement layer designed to support and safeguard trading engines. It implements configurable risk management policies that monitor, validate, and control trade activity in real-time to ensure regulatory compliance and system stability.",
  },
  {
    mainHeadingStatic: "Environment",
    mainGridIdStatic: "environment",
    mainHeadingDescStatic:
      "To start working with the Risk & Margin APIs, use the designated base URL along with your access credentials. Throughout this guide, the base URL will be referenced as <base_url>. All API responses are returned in JSON format with the application/json content type.",
  },

  // ----------------------------- Authenticate -----------------------------
  {
    mainHeading: "Authenticate",
    mainGridId: "authenticate",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Authenticate endpoint allows a user to log in by validating their credentials. Upon successful authentication, it returns access and refresh tokens along with session and user information for secure API access.",
    endpointId: "authenticate-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Authenticate",
    requestHeader: "Request Headers",
    requestHeaderId: "authenticate-headers",
    requestHeaderTable: { columns: headerColumns, rows: contentTypeHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "authenticate-request-body",
    requestObject: `{
  "userName": "String", // Optional
  "email": "String",
  "password": "String"
}`,
    requestBodyTable: {
      columns: fieldColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("userName"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("The username assigned to the user.") },
        { num: cellText("2"), fieldName: cellRequired("email"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("The user's email address. Ensure it is a valid email to avoid a 401 Unauthorized error.") },
        { num: cellText("3"), fieldName: cellRequired("password"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("The password used for authentication.") },
      ],
    },
    respObjKey: "grisk_authenticate_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Table 1 – Top-Level Login Response Fields",
        table: respTable([
          ["isSuccess", "Boolean", "Indicates whether the login request was successful."],
          ["errors", "Array", "List of error messages (empty if login is successful)."],
          ["role", "String", "Role assigned to the authenticated user."],
          ["idleTimeout", "String", "Duration of inactivity (in minutes) before session expires."],
          ["loginTFA", "Boolean", "Indicates if Two-Factor Authentication is enabled."],
          ["message", "String", "Human-readable message about the login result."],
          ["passwordExpirationDaysRemaining", "String", "Days remaining before the user's password expires."],
        ]),
      },
      {
        title: "Table 2 – Nested data Object Fields",
        table: respTable([
          ["data.userId", "String", "Unique identifier of the user."],
          ["data.userName", "String", "Username used for login."],
          ["data.clientName", "String", "Name of the client application/system."],
          ["data.email", "String", "User's email address."],
          ["data.accessToken", "String", "JWT access token used for authorized API requests."],
          ["data.refreshToken", "String", "Token used to obtain a new access token without re-login."],
          ["data.refreshTokenExpiration", "DateTime", "Expiration timestamp of the refresh token."],
          ["data.accessTokenExpiration", "DateTime", "Expiration timestamp of the access token."],
          ["data.sessionID", "String", "Unique session identifier for tracking."],
        ]),
      },
      {
        status: "Status Code: 401 (Un Authorized)",
        statusClass: "code-red",
        code: `{
  "isSuccess": false,
  "errors": ["Invalid Credentials"],
  "idleTimeout": 100,
  "loginTFA": false,
  "message": "One or more validation errors occurred."
}`,
        table: respTable([
          ["isSuccess", "Boolean", "true or false."],
          ["errors", "String", "A description of the type of error."],
          ["idleTimeout", "Integer", "Session timeout duration (if provided)."],
          ["loginTFA", "Boolean", "Indicates if Two-Factor Authentication is enabled (default is false)."],
          ["message", "String", "Describes the reason for failure or summary of the action."],
        ]),
      },
    ],
  },

  // ----------------------------- Validate OTP -----------------------------
  {
    mainHeading: "Validate OTP",
    mainGridId: "validate-otp",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/ValidateOTP endpoint verifies the code sent to the user for email or account confirmation. Upon successful verification, it confirms user identity or completes a two-step validation process.",
    endpointId: "validate-otp-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/ValidateOTP",
    requestHeader: "Request Headers",
    requestHeaderId: "validate-otp-headers",
    requestHeaderTable: { columns: headerColumns, rows: contentTypeHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "validate-otp-request-body",
    requestObject: `{
  "userName": "String", // Optional
  "email": "String",
  "code": "String"
}`,
    requestBodyTable: {
      columns: fieldColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("userName"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("The username assigned to the user.") },
        { num: cellText("2"), fieldName: cellRequired("email"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("The user's email address. Ensure the email is valid to avoid a 401 Unauthorized error.") },
        { num: cellText("3"), fieldName: cellRequired("code"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Numeric code.") },
      ],
    },
    respObjKey: "grisk_validate_otp_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Table 1 – Top-Level Response Fields",
        table: respTable([
          ["isSuccess", "Boolean", "Indicates whether the login attempt was successful."],
          ["errors", "Array", "Array of error messages (empty if login succeeds)."],
          ["role", "String", "Role assigned to the user upon successful authentication."],
          ["idleTimeout", "String", "Duration (in minutes) before the session times out."],
          ["loginTFA", "Boolean", "Specifies if Two-Factor Authentication is required or enabled."],
          ["message", "String", "Descriptive message conveying the result of the login attempt."],
        ]),
      },
      {
        title: "Table 2 – Nested data Object Fields",
        table: respTable([
          ["data.userId", "String", "Unique identifier associated with the authenticated user."],
          ["data.userName", "String", "Username provided for login."],
          ["data.clientName", "String", "Name of the client system or application."],
          ["data.email", "String", "Email address linked to the user account."],
          ["data.code", "String", "Verification code sent for additional validation steps."],
        ]),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "errors": {
    "email": [
      "Email and Code cannot be null, empty, or contain only spaces."
    ]
  },
  "type": "",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "traceId": "00-c09074e67adb5c6b1f409bed26ea603d-8fe7b4befac3cd5a-00"
}`,
        table: respTable([
          ["errors", "Object", "A dictionary of fields and their corresponding validation error messages."],
          ["email", "Array", "A list of error messages related to the email field. Example: Email and Code cannot be null, empty, or contain only spaces."],
          ["type", "String", "Here it refers to HTTP status code 400 (Bad Request)."],
          ["title", "String", "A human-readable summary of the problem."],
          ["status", "Integer", "HTTP status code. Example: 400."],
          ["traceId", "String", "A unique identifier for tracing the error in the backend logs."],
        ]),
      },
    ],
  },

  // ----------------------------- Resend OTP -----------------------------
  {
    mainHeading: "Resend OTP",
    mainGridId: "resend-otp",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Authenticate/ResendOTP endpoint sends a new One-Time Password (OTP) to the registered email address for the specified user. Typically used after a login attempt when the original OTP has expired or was not received.",
    endpointId: "resend-otp-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Authenticate/ResendOTP",
    requestHeader: "Request Headers",
    requestHeaderId: "resend-otp-headers",
    requestHeaderTable: { columns: headerColumns, rows: contentTypeHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "resend-otp-request-body",
    requestObject: `{
  "userName": "String", // Optional
  "email": "String",
  "password": "String"
}`,
    requestBodyTable: {
      columns: fieldColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("userName"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("The username assigned to the user.") },
        { num: cellText("2"), fieldName: cellRequired("email"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("The user's email address. Ensure it is valid to avoid a 401 Unauthorized error.") },
        { num: cellText("3"), fieldName: cellRequired("password"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("User password or the verification code required for the authentication or confirmation process.") },
      ],
    },
    respObjKey: "grisk_resend_otp_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Table 1 – Top-Level Response Fields",
        table: respTable([
          ["isSuccess", "Boolean", "Indicates whether the login attempt was successful."],
          ["errors", "Array", "Array of error messages (empty if login succeeds)."],
          ["role", "String", "Role assigned to the user upon successful authentication."],
          ["idleTimeout", "String", "Duration (in minutes) before the session times out."],
          ["loginTFA", "Boolean", "Specifies if Two-Factor Authentication is required or enabled."],
          ["message", "String", "Descriptive message conveying the result of the login attempt."],
        ]),
      },
      {
        title: "Table 2 – Nested data Object Fields",
        table: respTable([
          ["data.userId", "String", "Unique identifier associated with the authenticated user."],
          ["data.userName", "String", "Username provided for login."],
          ["data.clientName", "String", "Name of the client system or application."],
          ["data.email", "String", "Email address linked to the user account."],
          ["data.code", "String", "Verification code sent for additional validation steps."],
        ]),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "isSuccess": false,
  "errors": [
    "The Email field is not a valid e-mail address.",
    "The field Email must be a String with a minimum length of 7 and a maximum length of 320."
  ],
  "idleTimeout": 100,
  "loginTFA": true,
  "message": "One or more validation errors occurred."
}`,
        table: respTable([
          ["isSuccess", "String", "Indicates if the operation was successful (true or false)."],
          ["errors", "String", "A description of the type of error, if any."],
          ["idleTimeout", "Integer", "Session timeout duration (if provided)."],
          ["loginTFA", "Boolean", "Indicates if Two-Factor Authentication is enabled (default is false)."],
          ["message", "String", "Describes the reason for failure or provides a success message."],
        ]),
      },
    ],
  },

  // ----------------------------- Refresh Access Token -----------------------------
  {
    mainHeading: "Refresh Access Token",
    mainGridId: "refresh-token",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Authenticate/RefreshToken Exchanges a valid refresh token for a new access token (JWT). Also returns an updated refresh token, their expiry timestamps, and session information.",
    endpointId: "refresh-token-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Authenticate/RefreshToken",
    requestHeader: "Request Headers",
    requestHeaderId: "refresh-token-headers",
    requestHeaderTable: { columns: headerColumns, rows: contentTypeHeaderRows },
    requestBody: "Request Body",
    requestBodyId: "refresh-token-request-body",
    requestBodyTable: {
      columns: fieldColumnsWithRequired,
      rows: [
        { num: cellText("1"), fieldName: cellRequired("refreshToken"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Refresh token.") },
      ],
    },
    respObjKey: "grisk_refresh_token_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Table 1 – Top-Level Response Fields",
        table: respTable([
          ["Type", "String", "A URI reference that identifies the problem type (\"about:blank\" for generic issues)."],
          ["Title", "String|Null", "Short, human-readable summary of the problem (null in this response)."],
          ["Status", "Integer", "HTTP status code — 400 indicates Bad Request."],
          ["Detail", "String|Null", "Additional detail of the error (null in this response)."],
          ["Instance", "String", "The request path where the error occurred (/api/v1/RiskProfile/downloadSymbolFile)."],
          ["Extensions", "Object", "Container for extra diagnostic information (includes an embedded exception Object)."],
        ]),
      },
      {
        title: "Table 2 – Nested data Object Fields",
        table: respTable([
          ["ClassName", "String", "Exception class name (System.IO.FileNotFoundException)."],
          ["Message", "String", "Detailed message — file not found in the specified directory."],
          ["StackTraceString", "String", "Stack trace for debugging (truncated here as \"Stack Trace\")."],
          ["HResult", "Integer", "Numeric error code (-2147024894)."],
          ["Source", "String", "Source assembly that raised the exception (RiskServices)."],
          ["Other fields (Data, InnerException, HelpURL, etc.)", "—", "Additional .NET exception metadata (all null in this example)."],
        ]),
      },
      {
        status: "Status Code: 401 (Un Authorized)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Invalid refresh token.",
  "status": 401,
  "detail": "Refresh token has expired or is no longer valid.",
  "instance": "/api/v1/Authenticate/RefreshToken",
  "extensions": {}
}`,
        table: respTable([
          ["type", "String", "A URI reference identifying the error type (e.g., \"about:blank\")."],
          ["title", "String", "A short, human-readable summary of the problem (\"Invalid refresh token.\")."],
          ["status", "Integer", "HTTP status code (401 – Unauthorized)."],
          ["detail", "String", "Detailed explanation of the issue."],
          ["instance", "String", "The request path where the error occurred."],
          ["extensions", "Object", "Additional metadata for the error (empty in this case)."],
        ]),
      },
    ],
  },

  // ============================== Risk Profile ==============================
  {
    mainGridIdStatic: "risk-profile",
    navLabel: "Risk Profile",
  },
  {
    mainHeading: "Create Risk Profile",
    mainGridId: "create-risk-profile",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/RiskProfile endpoint creates a new risk profile based on the provided input data. It stores risk-related attributes for further analysis and decision-making.",
    endpointId: "create-risk-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/RiskProfile",
    requestHeader: "Request Headers",
    requestHeaderId: "create-risk-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBodyId: "create-risk-profile-body",
    bodyBlocks: riskProfileBodyBlocks,
    respObjKey: "grisk_create_risk_profile_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable(riskProfileFullRespRows),
      },
      riskProfile400Table,
    ],
  },
  {
    mainHeading: "GET All Risk Profile",
    mainGridId: "get-all-risk-profile",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/RiskProfile?<DataSourceLoadOptions> endpoint retrieves the details of all risk profiles, including their pre-trade and post-trade policy configurations, usage assignments, and creation metadata.",
    endpointId: "get-all-risk-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/RiskProfile?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-all-risk-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-all-risk-profile-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("(Query) Optional — Used for pagination, filtering, and sorting (DevExtreme format).") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_all_risk_profiles_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable([
          ["data", "Array", "Array of risk profile Objects."],
          ["id", "String", "Unique identifier for the risk profile."],
          ["name", "String", "Name of the risk profile."],
          ["pre_trade_policies", "Array", "Policies enforced before trades. Contains cfg and action."],
          ["POST_trade_policies", "Array", "Policies enforced after trades. May include nested layers."],
          ["used_by", "Object", "Contains lists of firms, groups, or traders associated with this profile."],
          ["assigned_firm", "Integer", "ID of the assigned firm (0 = not assigned)."],
          ["assigned_group", "Integer", "ID of the assigned group (0 = not assigned)."],
          ["assigned_account", "Integer", "ID of the assigned account (0 = not assigned)."],
          ["createdDate", "DateTime", "Timestamp when created."],
          ["updatedDate", "DateTime", "Timestamp when last updated."],
          ["totalCount", "Integer", "Total number of risk profiles available. Example: 100."],
          ["groupCount", "Integer", "May represent number of grouped records (often -1 if not grouped)."],
        ]),
      },
      badRequestTable,
    ],
  },
  {
    mainHeading: "GET Risk Profile By Id",
    mainGridId: "get-risk-profile-by-id",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/RiskProfile/<riskProfileId> endpoint retrieves the risk profile based on the provided ID.",
    endpointId: "get-risk-profile-by-id-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/RiskProfile/<riskProfileId>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-risk-profile-by-id-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-risk-profile-by-id-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("riskProfileId"), description: cellCaption("Example risk profile id: ABCProfile") },
      ],
    },
    respObjKey: "grisk_get_risk_profile_by_id_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable([["data", "Array", "Array of risk profile objects."], ...riskProfileRespRows]),
      },
      badRequestTable,
    ],
  },
  {
    mainHeading: "Delete Risk Profile By Id",
    mainGridId: "delete-risk-profile",
    mainHeadingDesc:
      "The DELETE <base_url>/api/v1/RiskProfile/<RiskProfileId> endpoint deletes a risk profile by its unique identifier. If the profile is successfully deleted, a 204 No Content status is returned. ",
    endpointId: "delete-risk-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "DELETE",
    endPointDesc: "<base_url>/api/v1/RiskProfile/<RiskProfileId>",
    requestHeader: "Request Headers",
    requestHeaderId: "delete-risk-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "delete-risk-profile-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("RiskProfileId"), description: cellCaption("The unique identifier of the risk profile to be deleted. Example: TEST09022024") },
      ],
    },
    responseTables: [
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Risk profile deleted successfully. No content is returned in the response.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "detail": "Risk profile associated with firm(s), trader group(s) and/or trader(s).",
  "instance": "/RiskProfile/{RiskProfileId}",
  "extensions": {}
}`,
        table: respTable([
          ["type", "String", "A URI reference that identifies the problem type (e.g., \"about:blank\" for generic issues)."],
          ["title", "String", "A short, human-readable summary of the problem – indicates a validation failure."],
          ["status", "Integer", "HTTP status code – 400 means Bad Request."],
          ["detail", "String", "Specific explanation of the problem."],
          ["instance", "String", "The request path where the error occurred."],
          ["extensions", "Object", "Optional additional data (empty in this case)."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET All Policies",
    mainGridId: "get-all-policies",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/RiskProfile/policies endpoint retrieves a predefined list of all supported risk policy names that can be used when creating or updating a risk profile. These policies define various trading constraints and limits applied pre- or post-trade.",
    endpointId: "get-all-policies-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/RiskProfile/policies",
    requestHeader: "Request Headers",
    requestHeaderId: "get-all-policies-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    respObjKey: "grisk_get_all_policies_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Returns a list of all available risk policy names.",
      },
      badRequestTable,
    ],
  },
  {
    mainHeading: "Update Risk Profile",
    mainGridId: "update-risk-profile",
    mainHeadingDesc:
      "The PUT <base_url>/api/v1/RiskProfile/<riskProfileId> endpoint updates an existing risk profile with the provided input data. This includes risk-related pre-trade and post-trade policies, used for compliance and trade monitoring.",
    endpointId: "update-risk-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/RiskProfile/<riskProfileId>",
    requestHeader: "Request Headers",
    requestHeaderId: "update-risk-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBodyId: "update-risk-profile-body",
    bodyBlocks: updateRiskProfileBodyBlocks,
    respObjKey: "grisk_update_risk_profile_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable(updateRiskProfileRespRows),
      },
      updateRiskProfile400Table,
    ],
  },
  {
    mainHeading: "Export Trader Risk Profile Settings",
    mainGridId: "export-trader-risk-settings",
    mainHeadingDesc:
      "This API call generates a CSV file containing configuration mappings.",
    endpointId: "export-trader-risk-settings-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/RiskProfile/policies",
    requestHeader: "Request Headers",
    requestHeaderId: "export-trader-risk-settings-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        table: {
          columns: [
            { Header: "Header", accessor: "fieldName", align: "left", width: "32%" },
            { Header: "Value", accessor: "description", align: "left" },
          ],
          rows: [
            { fieldName: cellText("Content-Type"), description: cellCaption("text/csv") },
            { fieldName: cellText("Content-Disposition"), description: cellCaption('attachment; filename="symbols.csv"') },
          ],
        },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Unable to export trader configuration settings.",
  "status": 400,
  "detail": "File generation failed due to missing risk profiles.",
  "instance": "/api/v1/RiskProfile/export_trader_configuration_settings",
  "extensions": {}
}`,
        table: respTable([
          ["Type", "String", "A URI reference that identifies the problem type (\"about:blank\" for generic issues)."],
          ["Title", "String|Null", "Short, human-readable summary of the problem (null in this response)."],
          ["Status", "Integer", "HTTP status code — 400 indicates Bad Request."],
          ["Detail", "String|Null", "Additional detail of the error (null in this response)."],
          ["Instance", "String", "The request path where the error occurred (/api/v1/RiskProfile/export_trader_configuration_settings)."],
          ["Extensions", "Object", "Container for extra diagnostic information (includes an embedded exception Object)."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Validate Symbol File",
    mainGridId: "validate-symbol-file",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/RiskProfile/validateSymbolFile?isStock=true endpoint uploads a CSV file of ticker symbols and returns which symbols are valid and which are invalid according to the system's rules.",
    endpointId: "validate-symbol-file-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/RiskProfile/validateSymbolFile?isStock=true",
    requestHeader: "Request Headers",
    requestHeaderId: "validate-symbol-file-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "validate-symbol-file-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("isStock"), description: cellCaption("Query (optional) — true = validate as stock symbols (default), false = options symbols.") },
        { num: cellText("2"), fieldName: cellText("file"), description: cellCaption("Form-data (multipart) — the symbol file (symbol.csv) to be validated.") },
      ],
    },
    bodyBlocks: [
      {
        table: {
          columns: [
            { Header: "Part Name", accessor: "num", align: "left", width: "20%" },
            { Header: "Type", accessor: "fieldName", align: "left", width: "16%" },
            { Header: "Required", accessor: "paramType", align: "center", width: "14%" },
            { Header: "Description", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("csv symbols"), fieldName: cellText("file (.csv)"), paramType: cellCaption("Yes"), description: cellCaption("The CSV file containing symbol overrides.") },
          ],
        },
      },
    ],
    respObjKey: "grisk_validate_symbol_file_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "When validation completes, the service returns two arrays:",
        list: [
          "validSymbols – symbols that passed all checks",
          "invalidSymbols – symbols that failed format or business-rule validation",
        ],
        tableTitle: "Response Field Table",
        table: respTable([
          ["validSymbols", "Array", "Symbols that passed validation."],
          ["invalidSymbols", "Array", "Symbols that failed validation."],
        ]),
      },
      {
        status: "Status Code: 400 (Not Acceptable)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Invalid symbol file.",
  "status": 400,
  "detail": "Header row missing or file not provided.",
  "instance": "/api/v1/RiskProfile/validateSymbolFile",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Download Symbol File",
    mainGridId: "download-symbol-file",
    mainHeadingDesc:
      "It returns a ready‑made CSV template (restrictedSymbols.csv) containing the correct header row for uploading or validating restricted symbols. If the file cannot be generated, a 400 Bad Request is returned.",
    endpointId: "download-symbol-file-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/RiskProfile/downloadSymbolFile",
    requestHeader: "Request Headers",
    requestHeaderId: "download-symbol-file-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        table: {
          columns: [
            { Header: "Header", accessor: "fieldName", align: "left", width: "32%" },
            { Header: "Value", accessor: "description", align: "left" },
          ],
          rows: [
            { fieldName: cellText("Content-Type"), description: cellCaption("text/csv") },
            { fieldName: cellText("Content-Disposition"), description: cellCaption('attachment; filename="symbols.csv"') },
          ],
        },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Unable to generate symbol file.",
  "status": 400,
  "detail": "File does not exist in the server directory.",
  "instance": "/api/v1/RiskProfile/downloadSymbolFile",
  "extensions": {}
}`,
        table: respTable([
          ["Type", "String", "A URI reference that identifies the problem type (\"about:blank\" for generic issues)."],
          ["Title", "String|Null", "Short, human-readable summary of the problem (null in this response)."],
          ["Status", "Integer", "HTTP status code — 400 indicates Bad Request."],
          ["Detail", "String|Null", "Additional detail of the error (null in this response)."],
          ["Instance", "String", "The request path where the error occurred (/api/v1/RiskProfile/downloadSymbolFile)."],
          ["Extensions", "Object", "Container for extra diagnostic information (includes an embedded exception Object)."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Export Symbol File",
    mainGridId: "export-symbol-file",
    mainHeadingDesc:
      "The ExportSymbolFile API endpoint allows users to export a symbol file associated with a specific risk profile. This POST  request retrieves the symbol data in a downloadable format for further analysis or backup.",
    endpointId: "export-symbol-file-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/RiskProfile/ExportSymbolFile",
    requestHeader: "Request Headers",
    requestHeaderId: "export-symbol-file-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `["AAPL", "GOOG", "TSLA"]`,
    requestBodyId: "export-symbol-file-resp",
    bodyBlocks: [
      { zone: "lead", heading: "Request Body", text: "The request body is a JSON array of symbol strings to export." },
    ],
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        table: {
          columns: [
            { Header: "Header", accessor: "fieldName", align: "left", width: "32%" },
            { Header: "Value", accessor: "description", align: "left" },
          ],
          rows: [
            { fieldName: cellText("Content-Type"), description: cellCaption("text/csv") },
            { fieldName: cellText("Content-Disposition"), description: cellCaption('attachment; filename="symbols.csv"') },
          ],
        },
      },
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Returned if the symbol list is empty or failed to export.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Invalid symbol input.",
  "status": 400,
  "detail": "Symbol list is empty or contains invalid characters.",
  "instance": "/api/v1/RiskProfile/ExportSymbolFile"
}`,
        table: respTable([
          ["Type", "String", "A URI reference that identifies the problem type (\"about:blank\" for generic issues)."],
          ["Title", "String|Null", "Short, human-readable summary of the problem (null in this response)."],
          ["Status", "Integer", "HTTP status code — 400 indicates Bad Request."],
          ["Detail", "String|Null", "Additional detail of the error (null in this response)."],
          ["Instance", "String", "The request path where the error occurred (/api/v1/RiskProfile/ExportSymbolFile)."],
          ["Extensions", "Object", "Container for extra diagnostic information (includes an embedded exception Object)."],
        ]),
      },
    ],
  },

  // ============================== Margin Profile ==============================
  {
    mainGridIdStatic: "margin-profile",
    navLabel: "Margin Profile",
  },
  {
    mainHeading: "Create Margin Profile",
    mainGridId: "create-margin-profile",
    mainHeadingDesc: "This API call creates a new margin profile.",
    endpointId: "create-margin-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/MarginProfile",
    requestHeader: "Request Headers",
    requestHeaderId: "create-margin-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "create-margin-profile-body",
    requestObject: `{
  "name": "TestMarginProfile012",
  "id": "TestMarginProfile012",
  "symbol_overrides": [],
  "maint_options_house_min_rate": 30,
  "maint_index_options_house_short_rate": 30,
  "maint_stock_options_house_short_rate": 30,
  "maint_stocks_house_short_rate": "30.00",
  "maint_stocks_house_Long_rate": "30.00",
  "initial_stocks_day_min_requirement": "40.00",
  "initial_stocks_regt_ovn_rate": 50.0,
  "initial_stocks_regt_day_rate": 25.0,
  "initial_stock_options_regt_short_rate": 20.0,
  "initial_index_options_regt_short_rate": 15.0,
  "initial_options_regt_min_rate": 10.0,
  "maint_stocks_regt_Long_rate": 25.0,
  "maint_stocks_regt_short_rate": 30.0,
  "maint_stock_under_2_5_regt_short_per_share": 2.5,
  "maint_stock_under_5_0_regt_short_rate": 100.0,
  "maint_stock_under_X_regt_short_per_share": 5.0,
  "maint_stock_options_regt_short_rate": 20.0,
  "maint_index_options_regt_short_rate": 15.0,
  "maint_options_regt_min_rate": 10.0
}`,
    requestBodyTable: {
      columns: fieldColumns,
      rows: marginFieldRows(marginFieldData),
    },
    respObjKey: "grisk_create_margin_profile_response",
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": null,
  "Title": "about:blank",
  "Status": 400,
  "Detail": "invalid margin profile data: invalid override for symbol OGDC: initial stocks ovn Long rate (1) < initial stocks regt ovn rate (50)",
  "Instance": "/margin_profiles",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "int", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Update Margin Profile",
    mainGridId: "update-margin-profile",
    mainHeadingDesc:
      "ThePUT <base_url>/api/v1/MarginProfile/<marginProfileId>Updates an existing margin profile (initial / maintenance rates, symbol overrides, and usage metadata) identified by profileId.",
    endpointId: "update-margin-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/MarginProfile/<marginProfileId>",
    requestHeader: "Request Headers",
    requestHeaderId: "update-margin-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "name": "TestMarginProfile012",
  "id": "TestMarginProfile012",
  "symbol_overrides": [],
  "maint_options_house_min_rate": 30,
  "maint_index_options_house_short_rate": 30,
  "maint_stock_options_house_short_rate": 30,
  "maint_stocks_house_short_rate": "30.00",
  "maint_stocks_house_Long_rate": "30.00",
  "initial_stocks_day_min_requirement": "40.00",
  "initial_stocks_regt_ovn_rate": 50.0,
  "initial_stocks_regt_day_rate": 25.0,
  "initial_stock_options_regt_short_rate": 20.0,
  "initial_index_options_regt_short_rate": 15.0,
  "initial_options_regt_min_rate": 10.0,
  "maint_stocks_regt_Long_rate": 25.0,
  "maint_stocks_regt_short_rate": 30.0,
  "maint_stock_under_2_5_regt_short_per_share": 2.5,
  "maint_stock_under_5_0_regt_short_rate": 100.0,
  "maint_stock_under_X_regt_short_per_share": 5.0,
  "maint_stock_options_regt_short_rate": 20.0,
  "maint_index_options_regt_short_rate": 15.0,
  "maint_options_regt_min_rate": 10.0
}`,
    requestBodyId: "update-margin-profile-params",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Parameter",
        table: {
          columns: paramColumns,
          rows: [
            { num: cellText("1"), fieldName: cellText("marginProfileId"), description: cellCaption("Example: TestMarginProfile012") },
          ],
        },
      },
      { heading: "Request Fields", table: { columns: fieldColumns, rows: marginFieldRows([["name", "Yes", "Name of margin Profile."], ...marginFieldData]) } },
    ],
    respObjKey: "grisk_create_margin_profile_response",
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "detail": "Field 'maint_options_house_min_rate' must be greater than 0.",
  "instance": "/api/v1/MarginProfile/122",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Margin Profile by ID",
    mainGridId: "get-margin-profile-by-id",
    mainHeadingDesc:
      "It allows the updates of an existing margin profile (initial / maintenance rates, symbol overrides, and usage metadata) identified by profileId.",
    endpointId: "get-margin-profile-by-id-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/MarginProfile/<marginProfileId>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-margin-profile-by-id-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-margin-profile-by-id-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("marginProfileId"), description: cellCaption("The unique identifier of the margin profile to retrieve.") },
      ],
    },
    respObjKey: "grisk_get_margin_profile_by_id_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable(marginProfileRespRows),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Margin profile not found.",
  "status": 404,
  "detail": "No margin profile with ID 12 exists.",
  "instance": "/api/v1/MarginProfile/12",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "int", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET All Margin Profile",
    mainGridId: "get-all-margin-profile",
    mainHeadingDesc:
      "It allows to retrives a list of all available margin profiles in the system, including their usage metadata, rates, and configuration.",
    endpointId: "get-all-margin-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/MarginProfile",
    requestHeader: "Request Headers",
    requestHeaderId: "get-all-margin-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    respObjKey: "grisk_get_all_margin_profiles_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable(marginProfileRespRows),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Margin profile not found.",
  "status": 404,
  "detail": "No margin profile with ID 12 exists.",
  "instance": "/api/v1/MarginProfile/12",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "int", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Delete Margin Profile",
    mainGridId: "delete-margin-profile",
    mainHeadingDesc:
      "DELETE <base_url>/api/v1/MarginProfile/<marginProfileId>removes a margin profile from the system. The profile cannot be deleted if it is still referenced by a firm, group, or trader.",
    endpointId: "delete-margin-profile-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "DELETE",
    endPointDesc: "<base_url>/api/v1/MarginProfile/<marginProfileId>",
    requestHeader: "Request Headers",
    requestHeaderId: "delete-margin-profile-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Path Parameter",
    requestBodyId: "delete-margin-profile-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("marginProfileId"), description: cellCaption("The unique identifier of the margin profile to delete.") },
      ],
    },
    responseTables: [
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Margin profile deleted successfully. No body returned.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Margin profile associated with firm(s)",
  "Instance": "/margin_profiles",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Download Margin Profile Symbol File",
    mainGridId: "download-margin-symbol-file",
    mainHeadingDesc:
      "GET <base_url>/api/v1/MarginProfile/downloadSymbolFile Downloads the symbol‑override template file used for margin‑profile symbol overrides. The file is returned as a CSV named symbols.csv",
    endpointId: "download-margin-symbol-file-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/MarginProfile/downloadSymbolFile",
    requestHeader: "Request Headers",
    requestHeaderId: "download-margin-symbol-file-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        table: {
          columns: [
            { Header: "Header", accessor: "fieldName", align: "left", width: "32%" },
            { Header: "Value", accessor: "description", align: "left" },
          ],
          rows: [
            { fieldName: cellText("Content-Type"), description: cellCaption("text/csv") },
            { fieldName: cellText("Content-Disposition"), description: cellCaption('attachment; filename="symbols.csv"') },
          ],
        },
      },
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Margin profile deleted successfully. No body returned.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": null,
  "Status": 400,
  "Detail": null,
  "Instance": "/api/v1/MarginProfile/downloadSymbolFile",
  "Extensions": {}
}`,
        table: respTable([
          ["Type", "String", "A URI reference that identifies the problem type (\"about:blank\" for generic issues)."],
          ["Title", "String|Null", "Short, human-readable summary of the problem (null in this response)."],
          ["Status", "Integer", "HTTP status code — 400 indicates Bad Request."],
          ["Detail", "String|Null", "Additional detail of the error (null in this response)."],
          ["Instance", "String", "The request path where the error occurred (/api/v1/MarginProfile/downloadSymbolFile)."],
          ["Extensions", "Object", "Container for extra diagnostic information (includes an embedded exception Object)."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Validate Symbol File",
    mainGridId: "validate-margin-symbol-file",
    mainHeadingDesc:
      "POST <base_url>/api/v1/<marginProfile>/validateSymbolFileUploads a CSV symbol file, validates its format/content, removes duplicate rows, and returns the cleaned‑up data (or a validation summary)",
    endpointId: "validate-margin-symbol-file-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/<marginProfile>/validateSymbolFile",
    requestHeader: "Request Headers",
    requestHeaderId: "validate-margin-symbol-file-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "validate-margin-symbol-file-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("marginProfile"), description: cellCaption("Request parameter — the unique identifier of the margin profile.") },
        { num: cellText("2"), fieldName: cellText("file"), description: cellCaption("Form-data (multipart) — the symbol file (symbol.csv) to be validated.") },
      ],
    },
    bodyBlocks: [
      {
        table: {
          columns: [
            { Header: "Part Name", accessor: "num", align: "left", width: "20%" },
            { Header: "Type", accessor: "fieldName", align: "left", width: "16%" },
            { Header: "Required", accessor: "paramType", align: "center", width: "14%" },
            { Header: "Description", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("csv symbols"), fieldName: cellText("file (.csv)"), paramType: cellCaption("Yes"), description: cellCaption("The CSV file containing symbol overrides.") },
          ],
        },
      },
    ],
    respObjKey: "grisk_validate_margin_symbol_file_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "When validation completes, the service returns two arrays:",
        list: [
          "validSymbols – symbols that passed all checks",
          "invalidSymbols – symbols that failed format or business-rule validation",
        ],
        tableTitle: "Response Field Table",
        table: respTable([
          ["validSymbols", "Array", "Symbols that passed validation."],
          ["invalidSymbols", "Array", "Symbols that failed validation."],
        ]),
      },
      {
        title: "Symbol Object (validSymbols[] / invalidSymbols[])",
        table: respTable([
          ["symbol", "String", "Ticker symbol from the CSV row."],
          ["data.initial_stocks_ovn_Long_rate", "Float", "Initial margin % for overnight long stock positions."],
          ["data.initial_stocks_ovn_short_rate", "Float", "Initial margin % for overnight short stock positions."],
          ["data.initial_stocks_day_Long_rate", "Float", "Initial margin % for day-trading long stock positions."],
          ["data.initial_stocks_day_short_rate", "Float", "Initial margin % for day-trading short stock positions."],
        ]),
        tip: "invalidSymbols also returns the row data so users know exactly what to fix.",
      },
      {
        status: "Status Code: 406 (Not Acceptable)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 406,
  "detail": "Invalid or missing headers.",
  "instance": "/api/v1/MarginProfile/validateSymbolFile",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },

  // ============================== Firm ==============================
  {
    mainGridIdStatic: "firm",
    navLabel: "Firm",
  },
  {
    mainHeading: "Create Firm",
    mainGridId: "create-firm",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Firm Creates a new firm along with its associated trading mode, risk profiles, and margin settings.",
    endpointId: "create-firm-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Firm",
    requestHeader: "Request Headers",
    requestHeaderId: "create-firm-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "create-firm-body",
    requestObject: `{
  "name": "TestFirm",
  "trading_mode": 0,
  "risk_profile": "Default_Risk_Profile",
  "default_group_risk_profile": "Default_Risk_Profile",
  "default_trader_risk_profile": "Default_Risk_Profile",
  "margin_profile": "Default_Margin_Profile",
  "has_equity_allocation": true,
  "account_type": 0,
  "pdt": true,
  "ovn_trading_multiple": 0,
  "day_trading_multiple": 0
}`,
    requestBodyTable: {
      columns: fieldColumnsWithRequired,
      rows: [
        { num: cellText("1"), fieldName: cellRequired("name"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique name of the firm.") },
        { num: cellText("2"), fieldName: cellRequired("trading_mode"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
        { num: cellText("3"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Default risk profile to associate with the firm.") },
        { num: cellText("4"), fieldName: cellText("default_group_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Risk profile for groups under this firm.") },
        { num: cellText("5"), fieldName: cellText("default_trader_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Default profile for newly created traders under this firm.") },
        { num: cellText("6"), fieldName: cellText("margin_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Associated margin profile name.") },
        { num: cellText("7"), fieldName: cellRequired("include_pnl_in_excess"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("include_pnl_in_excess value. Possible value 0,1,2 - do not include pnl, include pnl, include loss only respectively.") },
      ],
    },
    respObjKey: "grisk_create_firm_response",
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable([
          ["name", "String", "Name of the created firm."],
          ["trading_mode", "Integer", "Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively."],
          ["risk_profile", "String", "Firm's risk profile."],
          ["default_group_risk_profile", "String", "Group-level default risk profile."],
          ["default_trader_risk_profile", "String", "Default risk profile for all traders under the firm."],
          ["margin_profile", "String", "Margin Profile."],
          ["include_pnl_in_excess", "Integer", "include_pnl_in_excess value. Possible value 0,1,2 - do not include pnl, include pnl, include loss only respectively."],
        ]),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": null,
  "Status": 400,
  "Detail": null,
  "Instance": "/api/v1/Firm",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Invalid path params (firm, group, trader) or formatting."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Delete Firm",
    mainGridId: "delete-firm",
    mainHeadingDesc:
      "The DELETE <base_url>/api/v1/Firm/<firmId> removes a firm from the system by its numeric firmId.",
    endpointId: "delete-firm-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "DELETE",
    endPointDesc: "<base_url>/api/v1/Firm/<firmId>",
    requestHeader: "Request Headers",
    requestHeaderId: "delete-firm-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameter",
    requestBodyId: "delete-firm-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("firmId"), description: cellCaption("Unique numeric ID of the firm.") },
      ],
    },
    responseTables: [
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Firm deleted successfully. No body returned.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "errors": {
    "firmId": [
      "The value <firmId> is not valid."
    ]
  },
  "type": "",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "traceId": "00-e59ce03aed473c12ace2d5e74c89d9dd-f56018a0d81729e4-00"
}`,
        table: respTable([
          ["Title", "String", "Brief summary of the error."],
          ["Status", "Integer", "HTTP status code (400)."],
          ["Detail", "String", "Reason deletion failed (e.g., firm still linked to groups)."],
          ["Instance", "String", "Request path where the error occurred."],
          ["Extensions", "Object", "More description."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Update Firm",
    mainGridId: "update-firm",
    mainHeadingDesc:
      "The PUT <base_url>/api/v1/Firm/<firmId> updates an existing firm's core attributes: trading mode, risk profiles, margin profile, and P&L settings using the numeric firmId as a request parameter.",
    endpointId: "update-firm-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/Firm/<firmId>",
    requestHeader: "Request Headers",
    requestHeaderId: "update-firm-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "id": 1213,
  "name": "LSL Updated",
  "trading_mode": 1,
  "risk_profile": "LIVE_RISK",
  "default_group_risk_profile": "LIVE_RISK",
  "default_trader_risk_profile": "LIVE_RISK",
  "margin_profile": "LIVE_MARGIN",
  "include_pnl_in_excess": 1
}`,
    requestBodyId: "update-firm-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Parameter",
        table: {
          columns: [
            { Header: "#", accessor: "num", align: "left", width: "6%" },
            { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
            { Header: "Value (placeholder)", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("1"), fieldName: cellText("firmId"), description: cellCaption("ID of the firm (e.g., 1213)") },
          ],
        },
      },
      {
        heading: "Request Fields",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("name"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("New name for the firm.") },
            { num: cellText("2"), fieldName: cellRequired("trading_mode"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
            { num: cellText("3"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Firm risk profile.") },
            { num: cellText("4"), fieldName: cellText("default_group_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Risk profile assigned to new groups.") },
            { num: cellText("5"), fieldName: cellText("default_trader_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Risk profile assigned to new traders.") },
            { num: cellText("6"), fieldName: cellText("margin_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Margin Profile.") },
            { num: cellText("7"), fieldName: cellRequired("include_pnl_in_excess"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("include_pnl_in_excess value. Possible value 0,1,2 - do not include pnl, include pnl, include loss only respectively.") },
          ],
        },
      },
    ],
    respObjKey: "grisk_update_firm_response",
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable([
          ["id", "Long", "Numeric firm ID (unchanged)."],
          ["name", "String", "Updated firm name."],
          ["trading_mode", "Integer", "Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively."],
          ["risk_profile", "String", "Updated firm risk profile."],
          ["default_group_risk_profile", "String", "Updated default group risk profile."],
          ["default_trader_risk_profile", "String", "Updated default trader risk profile."],
          ["margin_profile", "String", "Updated margin profile."],
          ["include_pnl_in_excess", "Integer", "include_pnl_in_excess value. Possible value 0,1,2 - do not include pnl, include pnl, include loss only respectively."],
        ]),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Invalid path params (firm, group, trader) or formatting.",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Invalid path params (firm, group, trader) or formatting."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Update Firm Trading Mode",
    mainGridId: "update-firm-trading-mode",
    mainHeadingDesc:
      "The PUT <base_url>/api/v1/Firm/<firmId>/trading_mode endpoint sets a firm's trading mode.",
    endpointId: "update-firm-trading-mode-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/Firm/<firmId>/trading_mode",
    requestObject: `{
  "trading_mode": 1
}`,
    requestBodyId: "update-firm-trading-mode-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Path Parameter",
        table: {
          columns: [
            { Header: "#", accessor: "num", align: "left", width: "6%" },
            { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
            { Header: "Value (placeholder)", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("1"), fieldName: cellText("firmId"), description: cellCaption("ID of the firm whose trading mode is being updated.") },
          ],
        },
      },
      {
        heading: "Request Body",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("trading_mode"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
          ],
        },
      },
    ],
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
        title: "Trading mode updated. No response body is returned (null).",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Server cannot find the requested resource",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Trader Groups for a Firm",
    mainGridId: "get-trader-groups-for-firm",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/Firm/<traderGroupId>/trader_groups?<DataSourceLoadOptions> retrieves a paginated, sorted, grouped, and filtered list of trader groups associated with a specific firm using DevExtreme query parameters.",
    endpointId: "get-trader-groups-for-firm-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Firm/<traderGroupId>/trader_groups?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-trader-groups-for-firm-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Path Parameter",
    requestBodyId: "get-trader-groups-for-firm-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("firmId"), description: cellCaption("Example firmId: 12") },
        { num: cellText("2"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("Optional query parameters used by DevExtreme for filtering, sorting, paging, etc.") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_trader_groups_for_firm_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable([
          ["data", "Array", "List of trader group Objects."],
          ["data[].id", "Integer", "Numeric ID for the trader group."],
          ["data[].trader_group_id", "String", "Unique identifier of the trader group."],
          ["data[].name", "String", "Name of the trader group."],
          ["data[].firm_id", "String", "Firm ID associated with the group."],
          ["data[].trading_mode", "Integer", "Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively."],
          ["data[].risk_profile", "String", "Group-level risk profile."],
          ["data[].default_trader_risk_profile", "String", "Default risk profile assigned to traders in this group."],
          ["data[].createdTime", "DateTime", "Date the group was created."],
          ["data[].updatedTime", "DateTime", "Timestamp when last updated."],
          ["totalCount", "Integer", "Total number of records (often -1 when not paginated)."],
          ["groupCount", "Integer", "Number of grouped records (often -1 when not grouped)."],
        ]),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Invalid path params (firm, group, trader) or formatting.",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Invalid path params (firm, group, trader) or formatting."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET All Firms",
    mainGridId: "get-all-firms",
    mainHeadingDesc:
      "This API call returns a paginated, sorted, grouped, and filtered list of firms. The query string supports every DevExtreme DataSourceLoadOptions flag, enabling server-side paging, sorting, filtering, grouping, and summary aggregation.",
    endpointId: "get-all-firms-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Firm?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-all-firms-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-all-firms-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("Optional query parameters used by DevExtreme for filtering, sorting, paging, etc.") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_all_firms_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Firm Object Fields",
        table: respTable(firmRespRows),
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "someting went wrong",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Firm by Firm ID",
    mainGridId: "get-firm-by-id",
    mainHeadingDesc:
      "The Get Firm by Firm ID API endpoint retrieves detailed information about a specific firm using its unique firmId. This GET request returns firm-related data such as name, status, and configuration details.",
    endpointId: "get-firm-by-id-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Firm/<firmId>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-firm-by-id-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    respObjKey: "grisk_get_firm_by_id_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable(firmRespRows),
      },
      {
        status: "Status Code: 404 (Not Found)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Firm not found.",
  "status": 404,
  "detail": "No firm with ID 12 exists.",
  "instance": "/api/v1/Firm/12",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code (typically 404)."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },

  // ============================== Trader Group ==============================
  {
    mainGridIdStatic: "trader-group",
    navLabel: "Trader Group",
  },
  {
    mainHeading: "Create Trader Group",
    mainGridId: "create-trader-group",
    mainHeadingDesc:
      "This API call creates a new trader group under an existing firm and assigns default risk settings for all traders that will belong to this group.",
    endpointId: "create-trader-group-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/TraderGroup",
    requestHeader: "Request Headers",
    requestHeaderId: "create-trader-group-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "create-trader-group-body",
    requestObject: `{
  "trader_group_id": "TraderGroup101",
  "name": "TraderGroup101",
  "firm_id": "LSL",
  "trading_mode": 0,
  "risk_profile": "TestRiskProfile",
  "default_trader_risk_profile": "TestRiskProfile",
  "has_equity_allocation": true,
  "account_type": 1,
  "pdt": true,
  "ovn_trading_multiple": 1,
  "day_trading_multiple": 1,
  "createdTime": "Time",
  "updatedTime": "Time",
  "created_time": 0,
  "updated_time": 0
}`,
    requestBodyTable: {
      columns: fieldColumnsWithRequired,
      rows: [
        { num: cellText("1"), fieldName: cellRequired("trader_group_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique identifier for the trader group.") },
        { num: cellText("2"), fieldName: cellText("name"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Name of the trader group.") },
        { num: cellText("3"), fieldName: cellRequired("firm_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Firm under which this group is created.") },
        { num: cellText("4"), fieldName: cellText("trading_mode"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("0 = unrestricted, 1 = liquidation only, 2 = suspended.") },
        { num: cellText("5"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Group-level risk profile.") },
        { num: cellText("6"), fieldName: cellText("default_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Default risk profile.") },
        { num: cellText("7"), fieldName: cellText("has_equity_allocation"), paramType: cellCaption("No"), dataType: badge("Boolean"), description: cellCaption("Has equity allocation.") },
        { num: cellText("8"), fieldName: cellText("account_type"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Account Type.") },
        { num: cellText("9"), fieldName: cellText("ovn_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("Ovn trading multiple.") },
        { num: cellText("10"), fieldName: cellText("day_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("Day Trading multiple.") },
        { num: cellText("11"), fieldName: cellText("createdTime"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Created Time.") },
        { num: cellText("12"), fieldName: cellText("updatedTime"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Updated Date Time.") },
        { num: cellText("13"), fieldName: cellText("updated_Time"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Epoch time.") },
        { num: cellText("14"), fieldName: cellText("updated_Time"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Epoch time.") },
      ],
    },
    respObjKey: "grisk_create_trader_group_response",
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
        title: "Response Fields",
        table: { columns: fieldColumnsWithRequired, rows: traderGroupFieldRows },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "detail": "trader_group_id must be unique within the firm.",
  "instance": "/api/v1/TraderGroup",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Update Trader Group",
    mainGridId: "update-trader-group",
    mainHeadingDesc:
      "This API call updates an existing trader group (name, trading mode, risk profiles, etc.) identified by traderGroupId.",
    endpointId: "update-trader-group-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/TraderGroup/<traderGroupId>",
    requestHeader: "Request Headers",
    requestHeaderId: "update-trader-group-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "Id": 333,
  "trader_group_id": "TraderGroup101",
  "name": "TraderGroup101",
  "firm_id": "LSL",
  "trading_mode": 0,
  "risk_profile": "TestRiskProfile",
  "default_trader_risk_profile": "TestRiskProfile",
  "has_equity_allocation": true,
  "account_type": 1,
  "pdt": true,
  "ovn_trading_multiple": 1,
  "day_trading_multiple": 1,
  "createdTime": "Time",
  "updatedTime": "Time",
  "created_time": 0,
  "updated_time": 0
}`,
    requestBodyId: "update-trader-group-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Parameter",
        table: {
          columns: [
            { Header: "#", accessor: "num", align: "left", width: "6%" },
            { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
            { Header: "Value Description", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("1"), fieldName: cellText("traderGroupId"), description: cellCaption("The unique identifier of the trader group.") },
          ],
        },
      },
      {
        heading: "Request Body",
        table: { columns: fieldColumnsWithRequired, rows: traderGroupFieldRows },
      },
    ],
    respObjKey: "grisk_update_trader_group_response",
    responseTables: [
      {
        status: "Status Code: 201 (Created)",
        statusClass: "code-success",
        title: "Response Fields",
        table: {
          columns: [
            { Header: "Field", accessor: "fieldName", align: "left", width: "30%" },
            { Header: "Description", accessor: "description", align: "left" },
          ],
          rows: [
            { fieldName: cellText("id"), description: cellCaption("Numeric group ID.") },
            { fieldName: cellText("trader_group_id, name"), description: cellCaption("Updated identifier names.") },
            { fieldName: cellText("trading_mode"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
            { fieldName: cellText("risk_profile"), description: cellCaption("Updated group-level risk profile.") },
            { fieldName: cellText("default_trader_risk_profile"), description: cellCaption("Updated default trader profile.") },
            { fieldName: cellText("updatedTime"), description: cellCaption("Updated Timestamp.") },
          ],
        },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Validation failed.",
  "status": 400,
  "detail": "trading_mode must be 0,1,2 or 3",
  "instance": "/api/v1/TraderGroup/2333",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET All Trader Groups",
    mainGridId: "get-all-trader-groups",
    mainHeadingDesc:
      "It returns a list of trader groups with support for advanced filtering, sorting, grouping, summaries, and pagination. This endpoint is compatible with DevExtreme-style data queries.",
    endpointId: "get-all-trader-groups-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/TraderGroup?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-all-trader-groups-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Query Parameters",
    requestBodyId: "get-all-trader-groups-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("Query parameter — used to pass filtering, paging, sorting options (DevExtreme format).") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_all_trader_groups_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: { columns: fieldColumnsWithRequired, rows: traderGroupFieldRows },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Validation failed.",
  "status": 400,
  "detail": "trading_mode must be 0,1,2 or unrestricted",
  "instance": "/api/v1/TraderGroup/2333",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Trader Group by ID",
    mainGridId: "get-trader-group-by-id",
    mainHeadingDesc:
      "It returns a list of trader groups with support for advanced filtering, sorting, grouping, summaries, and pagination. This endpoint is compatible with DevExtreme-style data queries.",
    endpointId: "get-trader-group-by-id-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/TraderGroup/<traderGroupId>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-trader-group-by-id-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-trader-group-by-id-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("traderGroupId"), description: cellCaption("Unique identifier of the trader group to fetch. Example: 65") },
      ],
    },
    respObjKey: "grisk_get_trader_group_by_id_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: { columns: fieldColumnsWithRequired, rows: traderGroupFieldRows },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "Validation failed.",
  "status": 400,
  "detail": "3",
  "instance": "String url",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Delete Trader Group",
    mainGridId: "delete-trader-group",
    mainHeadingDesc:
      "It allows to removes a trader‑group record by its numeric traderGroupId. The group cannot be deleted if it is still referenced by traders or firms.",
    endpointId: "delete-trader-group-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "DELETE",
    endPointDesc: "<base_url>/api/v1/TraderGroup/<traderGroupId>",
    requestHeader: "Request Headers",
    requestHeaderId: "delete-trader-group-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "delete-trader-group-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("traderGroupId"), description: cellCaption("Unique ID of the trader group to delete. Example: 65") },
      ],
    },
    responseTables: [
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Trader group deleted successfully. No body returned.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "detail": "Trader group is assigned to active traders and cannot be deleted.",
  "instance": "/api/v1/TraderGroup/12",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Update Trader Group Trading Mode",
    mainGridId: "update-trader-group-trading-mode",
    mainHeadingDesc:
      "It removes a trader‑group record by its numeric traderGroupId. The group cannot be deleted if it is still referenced by traders or firms.",
    endpointId: "update-trader-group-trading-mode-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/TraderGroup/<traderGroupId>/trading_mode",
    requestHeader: "Request Headers",
    requestHeaderId: "update-trader-group-trading-mode-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "trading_mode": 0
}`,
    requestBodyId: "update-trader-group-trading-mode-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Path Parameter",
        table: {
          columns: [
            { Header: "#", accessor: "num", align: "left", width: "6%" },
            { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
            { Header: "Value Description", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("1"), fieldName: cellText("traderGroupId"), description: cellCaption("Unique ID of the trader group. example 65") },
          ],
        },
      },
      {
        heading: "Request Body",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("trading_mode"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
          ],
        },
      },
    ],
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        code: `{
  "trading_mode": 1
}`,
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "detail": "Trading mode must be 0 or 1.",
  "instance": "/api/v1/TraderGroup/122/trading_mode",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Traders in Trader Group",
    mainGridId: "get-traders-in-group",
    mainHeadingDesc:
      "It returns a paginated / filtered / sorted list of traders that beLong to a specific Trader Group, using DevExtreme’s DataSourceLoadOptions query pattern.",
    endpointId: "get-traders-in-group-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/TraderGroup/<traderGroupId>/traders?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-traders-in-group-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameter",
    requestBodyId: "get-traders-in-group-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("traderGroupId"), description: cellCaption("Unique ID of the trader group, example 65") },
        { num: cellText("2"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("Used for pagination, filtering, and sorting (DevExtreme format).") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    bodyBlocks: [
      {
        table: {
          columns: fieldColumnsWithRequired,
          rows: [
            { num: cellText("1"), fieldName: cellText("trading_mode"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
          ],
        },
      },
    ],
    respObjKey: "grisk_get_traders_in_group_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Trader Object Fields (inside data[])",
        table: {
          columns: [
            { Header: "Field", accessor: "fieldName", align: "left", width: "26%" },
            { Header: "Type", accessor: "dataType", align: "center", width: "14%" },
            { Header: "Description", accessor: "description", align: "left" },
          ],
          rows: [
            { fieldName: cellText("id"), dataType: badge("int"), description: cellCaption("Unique Identifier") },
            { fieldName: cellText("trader_id / name"), dataType: badge("String"), description: cellCaption("Trader identifier / name") },
            { fieldName: cellText("firm_id / group_id"), dataType: badge("String"), description: cellCaption("Firm & trader-group the trader belongs to") },
            { fieldName: cellText("short_vs_box"), dataType: badge("Boolean"), description: cellCaption("Short-vs-box flag") },
            { fieldName: cellText("account_type"), dataType: badge("Integer"), description: cellCaption("Following values are supported (0,1,2,3,4)") },
            { fieldName: cellText("pdt"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
            { fieldName: cellText("ovn_trading_multiple"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
            { fieldName: cellText("day_trading_multiple"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
            { fieldName: cellText("trading_mode"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.ive") },
            { fieldName: cellText("risk_profile"), dataType: badge("String"), description: cellCaption("Assigned risk profile") },
            { fieldName: cellText("created_time / updated_time"), dataType: badge("Long"), description: cellCaption("Timestamp when last updated") },
          ],
        },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "type": "about:blank",
  "title": "One or more validation errors occurred.",
  "status": 400,
  "detail": "Invalid traderGroupId or query.",
  "instance": "Url",
  "extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },

  // ============================== Trader ==============================
  {
    mainGridIdStatic: "trader",
    navLabel: "Trader",
  },
  {
    mainHeading: "Create Trader",
    mainGridId: "create-trader",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Trader endpoint creates a new trader under a specific firm with account-level trading configuration.",
    endpointId: "create-trader-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Trader",
    requestHeader: "Request Headers",
    requestHeaderId: "create-trader-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Fields",
    requestBodyId: "create-trader-body",
    requestObject: `{
  "firm_id": "LSL",
  "trader_id": "PK_0014",
  "name": "PK_0014",
  "short_vs_box": true,
  "account_type": 0,
  "pdt": true,
  "ovn_trading_multiple": 2.0,
  "day_trading_multiple": 4.0,
  "trading_mode": 0,
  "risk_profile": "riskprofile"
}`,
    requestBodyTable: {
      columns: fieldColumnsWithRequired,
      rows: [
        { num: cellText("1"), fieldName: cellRequired("trader_group_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique identifier for the trader group.") },
        { num: cellText("2"), fieldName: cellRequired("firm_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Firm under which this group is created.") },
        { num: cellText("3"), fieldName: cellText("trading_mode"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
        { num: cellText("4"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Group-level risk profile.") },
        { num: cellText("5"), fieldName: cellText("default_trader_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Risk profile automatically applied to any new trader in this group.") },
        { num: cellText("6"), fieldName: cellText("short_vs_box"), paramType: cellCaption("True"), dataType: badge("Boolean"), description: cellCaption("") },
        { num: cellText("7"), fieldName: cellText("account_type"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Following values are supported (0,1,2,3,4)") },
        { num: cellText("8"), fieldName: cellText("ovn_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
        { num: cellText("9"), fieldName: cellText("day_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
        { num: cellText("10"), fieldName: cellText("pdt"), paramType: cellCaption("false"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
      ],
    },
    respObjKey: "grisk_create_trader_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellText("firm_id"), dataType: badge("String"), description: cellCaption("Identifier of the firm to which the trader belongs") },
            { num: cellText("2"), fieldName: cellText("trader_id"), dataType: badge("String"), description: cellCaption('Unique identifier for the trader (e.g., "PK_0014").') },
            { num: cellText("3"), fieldName: cellText("name"), dataType: badge("String"), description: cellCaption("Name of the trader (trader_id).") },
            { num: cellText("4"), fieldName: cellText("short_vs_box"), dataType: badge("Boolean"), description: cellCaption("Indicates if short vs. box strategy is enabled (true or false).") },
            { num: cellText("5"), fieldName: cellText("account_type"), dataType: badge("Integer"), description: cellCaption("Type of trading account") },
            { num: cellText("6"), fieldName: cellText("pdt"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
            { num: cellText("7"), fieldName: cellText("ovn_trading_multiple"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
            { num: cellText("8"), fieldName: cellText("day_trading_multiple"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
            { num: cellText("9"), fieldName: cellText("trading_mode"), dataType: badge("Integer"), description: cellCaption("0,1,2 - restricted") },
            { num: cellText("10"), fieldName: cellText("risk_profile"), dataType: badge("String"), description: cellCaption('Assigned risk profile name (e.g., "IALI").') },
            { num: cellText("11"), fieldName: cellText("created_time"), dataType: badge("Long"), description: cellCaption("Created Time") },
            { num: cellText("12"), fieldName: cellText("updated_time"), dataType: badge("Long"), description: cellCaption("Updated Time") },
          ],
        },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Trader already exist",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Detailed error message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Create Bulk Traders",
    mainGridId: "create-bulk-traders",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Trader/bulkCreate endpoint creates new traders in bulk under a specific firm with account-level trading configuration.",
    endpointId: "create-bulk-traders-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Trader/bulkCreate",
    requestHeader: "Request Headers",
    requestHeaderId: "create-bulk-traders-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `[
  {
    "firm_id": "LSL",
    "trader_id": "TR_0015",
    "name": "TR_0015",
    "short_vs_box": true,
    "account_type": 0,
    "pdt": true,
    "ovn_trading_multiple": 2.0,
    "day_trading_multiple": 4.0,
    "trading_mode": 0,
    "risk_profile": "riskprofile"
  },
  {
    "firm_id": "LSL",
    "trader_id": "TR_0014",
    "name": "TR_0014",
    "short_vs_box": true,
    "account_type": 0,
    "pdt": true,
    "ovn_trading_multiple": 2.0,
    "day_trading_multiple": 4.0,
    "trading_mode": 0,
    "risk_profile": "riskprofile"
  }
]`,
    requestBody: "Request Fields",
    requestBodyId: "create-bulk-traders-body",
    requestBodyTable: {
      columns: fieldColumnsWithRequired,
      rows: [
        { num: cellText("1"), fieldName: cellRequired("trader_group_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique identifier for the trader group.") },
        { num: cellText("2"), fieldName: cellRequired("firm_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Firm under which this group is created.") },
        { num: cellText("3"), fieldName: cellText("trading_mode"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
        { num: cellText("4"), fieldName: cellRequired("risk_profile"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Group-level risk profile.") },
        { num: cellText("5"), fieldName: cellText("default_trader_risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Risk profile automatically applied to any new trader in this group.") },
        { num: cellText("6"), fieldName: cellText("short_vs_box"), paramType: cellCaption("True"), dataType: badge("Boolean"), description: cellCaption("") },
        { num: cellText("7"), fieldName: cellText("account_type"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Following values are supported (0,1,2,3,4)") },
        { num: cellText("8"), fieldName: cellText("ovn_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
        { num: cellText("9"), fieldName: cellText("day_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
        { num: cellText("10"), fieldName: cellText("pdt"), paramType: cellCaption("false"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
      ],
    },
    respObjKey: "grisk_create_trader_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellText("firm_id"), dataType: badge("String"), description: cellCaption("Identifier of the firm to which the trader belongs") },
            { num: cellText("2"), fieldName: cellText("trader_id"), dataType: badge("String"), description: cellCaption('Unique identifier for the trader (e.g., "PK_0014").') },
            { num: cellText("3"), fieldName: cellText("name"), dataType: badge("String"), description: cellCaption("Name of the trader (trader_id).") },
            { num: cellText("4"), fieldName: cellText("short_vs_box"), dataType: badge("Boolean"), description: cellCaption("Indicates if short vs. box strategy is enabled (true or false).") },
            { num: cellText("5"), fieldName: cellText("account_type"), dataType: badge("Integer"), description: cellCaption("Type of trading account") },
            { num: cellText("6"), fieldName: cellText("pdt"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
            { num: cellText("7"), fieldName: cellText("ovn_trading_multiple"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
            { num: cellText("8"), fieldName: cellText("day_trading_multiple"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
            { num: cellText("9"), fieldName: cellText("trading_mode"), dataType: badge("Integer"), description: cellCaption("0,1,2 - restricted") },
            { num: cellText("10"), fieldName: cellText("risk_profile"), dataType: badge("String"), description: cellCaption('Assigned risk profile name (e.g., "IALI").') },
            { num: cellText("11"), fieldName: cellText("created_time"), dataType: badge("Long"), description: cellCaption("Created Time") },
            { num: cellText("12"), fieldName: cellText("updated_time"), dataType: badge("Long"), description: cellCaption("Updated Time") },
          ],
        },
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Trader already exist",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Detailed error message."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "Create Traders From CSV",
    mainGridId: "create-traders-csv",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Trader/createFromCSV endpoint creates new traders under a specific firm by uploading a CSV file with account-level trading configuration.",
    endpointId: "create-traders-csv-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Trader/createFromCSV",
    requestHeader: "Request Headers",
    requestHeaderId: "create-traders-csv-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: "<Upload CSV file>",
    requestBody: "Request Fields",
    requestBodyId: "create-traders-csv-body",
    requestBodyTable: { columns: fieldColumnsWithRequired, rows: traderRequestFieldRows },
    respObjKey: "grisk_create_traders_csv_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: traderResponse200Table,
      },
      traderBadRequest("Trader already exist", "/traders", "Detailed error message."),
    ],
  },
  {
    mainHeading: "GET All Trader",
    mainGridId: "get-all-trader",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/Trader?<DataSourceLoadOptions> endpoint retrieves all trader accounts available in the system. The response includes a paginated list of trader objects, along with total count metadata.",
    endpointId: "get-all-trader-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Trader?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-all-trader-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-all-trader-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("(Query) Optional — Used for pagination, filtering, and sorting (DevExtreme format).") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_all_trader_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Success Response Fields",
        table: { columns: respFieldColumns, rows: traderDataTableRows },
        tables: [
          { title: "Fields in each trader Object", columns: respFieldColumns, rows: traderObjectFieldRows },
        ],
      },
      traderBadRequest("Trader already exist", "/traders", "Detailed error message."),
    ],
  },
  {
    mainHeading: "GET All Trader by Passing Firm Names",
    mainGridId: "get-trader-by-firm-names",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Trader/<byFirmName>?<DataSourceLoadOptions> retrieves all trader accounts for the provided firm names. The response includes a paginated list of trader objects, along with total count metadata.",
    endpointId: "get-trader-by-firm-names-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Trader/<byFirmName>?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-trader-by-firm-names-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-trader-by-firm-names-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("(Query) Optional — Used for pagination, filtering, and sorting (DevExtreme format).") },
        { num: cellText("2"), fieldName: cellText("byFirmname"), description: cellCaption("Array of firms e.g. [LSL, RSK]") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_all_trader_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Success Response Fields",
        table: { columns: respFieldColumns, rows: traderDataTableRows },
        tables: [
          { title: "Fields in each trader Object", columns: respFieldColumns, rows: traderObjectFieldRows },
        ],
      },
      traderBadRequest("Trader already exist", "/traders", "Detailed error message."),
    ],
  },
  {
    mainHeading: "GET All Trader by Passing Firm IDs",
    mainGridId: "get-trader-by-firm-ids",
    mainHeadingDesc:
      "The POST <base_url>/api/v1/Trader/<byFirms>?<DataSourceLoadOptions> retrieves all trader accounts for the provided firm IDs. The response includes a paginated list of trader objects, along with total count metadata.",
    endpointId: "get-trader-by-firm-ids-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "POST",
    endPointDesc: "<base_url>/api/v1/Trader/<byFirms>?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-trader-by-firm-ids-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-trader-by-firm-ids-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("(Query) Optional — Used for pagination, filtering, and sorting (DevExtreme format).") },
        { num: cellText("2"), fieldName: cellText("byFirms"), description: cellCaption("Array of firms e.g. [7,8]") },
      ],
    },
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_all_trader_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Success Response Fields",
        table: { columns: respFieldColumns, rows: traderDataTableRows },
        tables: [
          { title: "Fields in each trader Object", columns: respFieldColumns, rows: traderObjectFieldRows },
        ],
      },
      traderBadRequest("Trader already exist", "/traders", "Detailed error message."),
    ],
  },
  {
    mainHeading: "Update Trader",
    mainGridId: "update-trader",
    mainHeadingDesc:
      "The PUT <base_url>/api/v1/Trader/<traderId> updates an existing trader's details such as margin multiples, risk profile, and group assignment.",
    endpointId: "update-trader-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/Trader/<traderId>",
    requestHeader: "Request Headers",
    requestHeaderId: "update-trader-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "id": 12813,
  "trader_id": "PK_0016",
  "group_id": "LSL_Default",
  "firm_id": "LSL",
  "short_vs_box": true,
  "trading_mode": 0,
  "risk_profile": "risk_profile",
  "name": "PK_0016",
  "account_type": 0,
  "pdt": true,
  "ovn_trading_multiple": 2.0,
  "day_trading_multiple": 4.0
}`,
    requestBodyId: "update-trader-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Parameters",
        table: {
          columns: [
            { Header: "#", accessor: "num", align: "left", width: "6%" },
            { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
            { Header: "value", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("1"), fieldName: cellText("traderId"), description: cellCaption("Example 12813") },
          ],
        },
      },
      {
        heading: "Request Body",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("id"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Unique Identifier") },
            { num: cellText("2"), fieldName: cellRequired("trader_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Unique identifier of the trader.") },
            { num: cellText("3"), fieldName: cellRequired("name"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Name of the trader.") },
            { num: cellText("4"), fieldName: cellRequired("group_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Group to which the trader is assigned.") },
            { num: cellText("5"), fieldName: cellRequired("firm_id"), paramType: cellCaption("Yes"), dataType: badge("String"), description: cellCaption("Identifier of the firm.") },
            { num: cellText("6"), fieldName: cellText("short_vs_box"), paramType: cellCaption("No"), dataType: badge("Boolean"), description: cellCaption("Indicates whether short vs box is allowed.") },
            { num: cellText("7"), fieldName: cellText("trading_mode"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
            { num: cellText("8"), fieldName: cellText("risk_profile"), paramType: cellCaption("No"), dataType: badge("String"), description: cellCaption("Assigned risk profile ID.") },
            { num: cellText("9"), fieldName: cellText("account_type"), paramType: cellCaption("No"), dataType: badge("Integer"), description: cellCaption("Following values are supported (0,1,2,3,4)") },
            { num: cellText("10"), fieldName: cellText("pdt"), paramType: cellCaption("No"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
            { num: cellText("11"), fieldName: cellText("ovn_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("Day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
            { num: cellText("12"), fieldName: cellText("day_trading_multiple"), paramType: cellCaption("No"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
          ],
        },
      },
    ],
    respObjKey: "grisk_update_trader_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellText("id"), dataType: badge("Integer"), description: cellCaption("Unique trader ID") },
            { num: cellText("2"), fieldName: cellText("trader_id"), dataType: badge("String"), description: cellCaption("Trader identifier") },
            { num: cellText("3"), fieldName: cellText("group_id"), dataType: badge("String"), description: cellCaption("Group assigned to trader") },
            { num: cellText("4"), fieldName: cellText("firm_id"), dataType: badge("String"), description: cellCaption("Firm ID") },
            { num: cellText("5"), fieldName: cellText("name"), dataType: badge("String"), description: cellCaption("Trader's name") },
            { num: cellText("6"), fieldName: cellText("short_vs_box"), dataType: badge("Boolean"), description: cellCaption("Indicates short vs box permission") },
            { num: cellText("7"), fieldName: cellText("trading_mode"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
            { num: cellText("8"), fieldName: cellText("risk_profile"), dataType: badge("String"), description: cellCaption("Risk profile ID") },
            { num: cellText("9"), fieldName: cellText("account_type"), dataType: badge("Integer"), description: cellCaption("Following values are supported (0,1,2,3,4)") },
            { num: cellText("10"), fieldName: cellText("pdt"), dataType: badge("Boolean"), description: cellCaption("Indicates if the trader is marked as a Pattern Day Trader (PDT).") },
            { num: cellText("11"), fieldName: cellText("ovn_trading_multiple"), dataType: badge("Float"), description: cellCaption("O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] default 2.0") },
            { num: cellText("12"), fieldName: cellText("day_trading_multiple"), dataType: badge("Float"), description: cellCaption("day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0") },
            { num: cellText("13"), fieldName: cellText("created_time"), dataType: badge("Long"), description: cellCaption("Created Time") },
            { num: cellText("14"), fieldName: cellText("updated_time"), dataType: badge("Long"), description: cellCaption("Updated Time") },
          ],
        },
      },
      traderBadRequest("Invalid payload (e.g., missing required fields, mismatched id)", "/traders", "Invalid payload (e.g., missing required fields, mismatched id)."),
    ],
  },
  {
    mainHeading: "Update Trader Mode",
    mainGridId: "update-trader-mode",
    mainHeadingDesc:
      "The PUT <base_url>/api/v1/Trader/<traderId>/trading_mode updates the trading_mode of a specific trader. This is typically used to switch between live, simulation, or restricted modes.",
    endpointId: "update-trader-mode-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/Trader/<traderId>/trading_mode",
    requestHeader: "Request Headers",
    requestHeaderId: "update-trader-mode-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "trading_mode": 0 /*Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.*/
}`,
    requestBodyId: "update-trader-mode-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Parameters",
        table: {
          columns: [
            { Header: "#", accessor: "num", align: "left", width: "6%" },
            { Header: "Key", accessor: "fieldName", align: "left", width: "28%" },
            { Header: "Value", accessor: "description", align: "left" },
          ],
          rows: [
            { num: cellText("1"), fieldName: cellText("traderId"), description: cellCaption("example traderId: 1213") },
          ],
        },
      },
      {
        heading: "Request Fields",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("trading_mode"), paramType: cellCaption("Yes"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
          ],
        },
      },
    ],
    respObjKey: "grisk_update_trader_mode_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellText("trading_mode"), dataType: badge("Integer"), description: cellCaption("Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively.") },
          ],
        },
      },
      traderBadRequest("Trader with the specified ID does not exist", "/traders/id/tradingmode", "Trader with the specified ID does not exist"),
    ],
  },
  {
    mainHeading: "GET Trader by Id",
    mainGridId: "get-trader-by-id",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/Trader/<traderId> retrieves detailed information of a trader by their traderId.",
    endpointId: "get-trader-by-id-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Trader/<traderId>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-trader-by-id-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-trader-by-id-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("traderId"), description: cellCaption("Example: 12812") },
      ],
    },
    respObjKey: "grisk_get_trader_by_id_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable(traderRespRows),
      },
      traderBadRequest("Trader with the specified ID does not exist", "/traders", "Trader with the specified ID does not exist"),
    ],
  },
  {
    mainHeading: "Delete Trader by ID",
    mainGridId: "delete-trader",
    mainHeadingDesc:
      "The DELETE <base_url>/api/v1/Trader/<traderId> deletes a trader from the system using their unique numeric traderId.",
    endpointId: "delete-trader-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "DELETE",
    endPointDesc: "<base_url>/api/v1/Trader/<traderId>",
    requestHeader: "Request Headers",
    requestHeaderId: "delete-trader-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "delete-trader-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("traderId"), description: cellCaption("Example traderId value: 1213") },
      ],
    },
    responseTables: [
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "No body is returned on success.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Trader with the specified ID does not exist",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Trader with the specified ID does not exist."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Traders by Firm ID(s)",
    mainGridId: "get-traders-by-firm",
    mainHeadingDesc:
      "The GET <base_url>api/v1/Trader/byFirms?<DataSourceLoadOptions>Returns a filtered, sorted, grouped, and paginated list of traders for given firm IDs. This endpoint supports DevExtreme’s DataSourceLoadOptions to enable powerful frontend grid operations like filtering, sorting, grouping, summary calculations, and virtual paging.",
    endpointId: "get-traders-by-firm-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Trader/byFirm?<DataSourceLoadOptions>",
    requestHeader: "Request Headers",
    requestHeaderId: "get-traders-by-firm-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestObject: `{
  "firmIds": [7, 8]
}`,
    requestBodyId: "get-traders-by-firm-body",
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Body",
        table: {
          columns: fieldColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("firmIds"), paramType: cellCaption("Yes"), dataType: badge("Long []"), description: cellCaption("List of firm IDs to retrieve associated traders") },
          ],
        },
      },
      {
        heading: "Request Parameters",
        table: {
          columns: paramColumns,
          rows: [
            { num: cellText("1"), fieldName: cellText("DataSourceLoadOptions"), description: cellCaption("(Query) Optional — Used for pagination, filtering, and sorting (DevExtreme format).") },
          ],
        },
      },
    ],
    requestBodyNote: dataSourceLoadOptionsNote,
    respObjKey: "grisk_get_traders_by_firm_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        tableTitle: "Response Fields",
        title: "Top-Level Fields",
        table: {
          columns: respFieldColumns,
          rows: respRows([
            ["data", "Array", "List of trader Objects matching the requested firm(s)."],
            ["totalCount", "Integer", "Total number of trader records returned."],
            ["groupCount", "Integer", "Number of groups (used for grouped results in UI)."],
          ]),
        },
        tables: [
          {
            title: "Trader Object",
            columns: respFieldColumns,
            rows: respRows([
              ["firm_id", "String", "Unique identifier of the firm."],
              ["id", "Integer", "Numeric ID of the trader record."],
              ["trader_id", "String", "Unique ID for the trader."],
              ["name", "String", "name of the trader (trader_id)."],
              ["group_id", "String", "Group to which the trader belongs."],
              ["short_vs_box", "Boolean", "Indicates if short vs. box trading is allowed."],
              ["account_type", "Integer", "Following values are supported (0,1,2,3,4)"],
              ["pdt", "Boolean", "Indicates if the trader is marked as a Pattern Day Trader (PDT)."],
              ["ovn_trading_multiple", "Float", "O/N trading multiple. Following values are supported must be in the interval [1.0, 2.0] but default is 2.0"],
              ["day_trading_multiple", "Float", "Day trading multiple. Following values are supported must be in the interval [1.0, 4.0] but default is 4.0"],
              ["trading_mode", "Integer", "Possible value 0,1,2 - unrestricted, liquidation only, suspended respectively."],
              ["risk_profile", "String", "Associated risk profile for the trader."],
              ["created_time", "Long", "Created Time."],
              ["updated_time", "Long", "Update Time."],
            ]),
          },
        ],
      },
      traderBadRequest("Trader with the specified ID does not exist", "/traders", "Trader with the specified ID does not exist"),
    ],
  },
  {
    mainHeading: "Update Trader's SOD Equity",
    mainGridId: "update-trader-sod-equity",
    mainHeadingDesc:
      "The PUT <base_url>/api/v1/Trader/state/firms/<firm_id>/groups/<group_id>/traders/<trader_id>/sod_equity updates the Start-of-Day (SOD) Equity for a specific trader within a given firm and group. This value is typically used for calculating intraday risk exposure or margin requirements.",
    endpointId: "update-trader-sod-equity-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "PUT",
    endPointDesc: "<base_url>/api/v1/Trader/state/firms/<firm_id>/groups/<group_id>/traders/<trader_id>/sod_equity",
    requestHeader: "Request Headers",
    requestHeaderId: "update-trader-sod-equity-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    bodyBlocks: [
      {
        zone: "lead",
        heading: "Request Parameters",
        table: {
          columns: paramColumns,
          rows: [
            { num: cellText("1"), fieldName: cellRequired("firm_id"), description: cellCaption("ID of the firm (e.g., RSK)") },
            { num: cellText("2"), fieldName: cellRequired("group_id"), description: cellCaption("ID of the trader group (e.g., RSK_GROUP)") },
            { num: cellText("3"), fieldName: cellRequired("trader_id"), description: cellCaption("ID of the trader (e.g., MCT)") },
          ],
        },
      },
    ],
    requestBody: "Request Fields",
    requestBodyId: "update-trader-sod-equity-body",
    requestObject: `{
  "sod_equity": 100000.00
}`,
    requestBodyTable: {
      columns: fieldColumnsWithRequired,
      rows: [
        { num: cellText("1"), fieldName: cellRequired("sod_equity"), paramType: cellCaption("Yes"), dataType: badge("Float"), description: cellCaption("Start-of-day equity amount.") },
      ],
    },
    responseTables: [
      {
        status: "Status Code: 204 (No Content)",
        statusClass: "accepted",
        title: "Trader's SOD Equity was successfully updated.",
      },
      {
        status: "Status Code: 400 (Bad Request)",
        statusClass: "code-red",
        code: `{
  "Type": "about:blank",
  "Title": "One or more validation errors occurred.",
  "Status": 400,
  "Detail": "Invalid path params (firm, group, trader) or formatting.",
  "Instance": "/traders",
  "Extensions": {}
}`,
        table: respTable([
          ["title", "String", "Human-readable title of the error."],
          ["status", "Integer", "HTTP status code."],
          ["detail", "String", "Invalid path params (firm, group, trader) or formatting."],
          ["instance", "String", "URI of the request that caused the error."],
        ]),
      },
    ],
  },
  {
    mainHeading: "GET Trader State",
    mainGridId: "get-trader-state",
    mainHeadingDesc:
      "The GET <base_url>/api/v1/Trader/state/firms/<firm_id>/groups/<group_id>/traders/<trader_id>/sod_equity returns the real-time state of a trader, including start-of-day (SOD) equity figures, current equity, excess values, and buying-power calculations.",
    endpointId: "get-trader-state-endpoint",
    endPointHeading: "Request Endpoint",
    endPointMethod: "GET",
    endPointDesc: "<base_url>/api/v1/Trader/state/firms/<firm_id>/groups/<group_id>/traders/<trader_id>/sod_equity",
    requestHeader: "Request Headers",
    requestHeaderId: "get-trader-state-headers",
    requestHeaderTable: { columns: headerColumns, rows: bearerHeaderRows },
    requestBody: "Request Parameters",
    requestBodyId: "get-trader-state-params",
    requestBodyTable: {
      columns: paramColumns,
      rows: [
        { num: cellText("1"), fieldName: cellText("firm_id"), description: cellCaption("ID of the firm (e.g., RSK)") },
        { num: cellText("2"), fieldName: cellText("group_id"), description: cellCaption("ID of the trader group (e.g., RSK_GROUP)") },
        { num: cellText("3"), fieldName: cellText("trader_id"), description: cellCaption("ID of the trader (e.g., MCT)") },
      ],
    },
    respObjKey: "grisk_get_trader_state_response",
    responseTables: [
      {
        status: "Status Code: 200 (Success)",
        statusClass: "code-success",
        title: "Response Fields",
        table: respTable([
          ["sod_equity", "Float", "Trader's start-of-day equity."],
          ["current_equity", "Float", "Current equity."],
          ["sod_ovn_excess", "Float", "SOD overnight excess."],
          ["sod_day_excess", "Float", "SOD day-trading excess."],
          ["sod_ovn_buying_power", "Float", "Overnight buying power."],
          ["sod_day_buying_power", "Float", "Day-trading buying power."],
          ["current_ovn_excess", "Float", "Current overnight excess."],
          ["current_day_excess", "Float", "Current day-trading excess."],
          ["current_ovn_bp", "Float", "Current overnight buying power."],
          ["current_day_bp", "Float", "Current day-trading buying power."],
          ["intraday_equity_adjustment", "Float", "Equity adjustments applied intraday."],
        ]),
      },
      traderBadRequest("Invalid path params (firm, group, trader) or formatting.", "/traders", "Invalid path params (firm, group, trader) or formatting."),
    ],
  },

  // ============================== Reference Tables ==============================
  {
    mainHeadingStatic: "Query Parameters (Data Source Load Options)",
    mainGridIdStatic: "query-parameters",
    mainHeadingDescStatic:
      "These are DevExtreme-specific query options used to control sorting, grouping, filtering, pagination, and summaries:",
    staticTables: [
      {
        half: true,
        columns: [
          { Header: "Query Param", accessor: "fieldName", align: "left", width: "32%" },
          { Header: "Description", accessor: "description", align: "left" },
        ],
        rows: queryParamRows,
      },
    ],
  },
  {
    mainHeadingStatic: "Pre Trade Policies",
    mainGridIdStatic: "pre-trade-policies",
    mainHeadingDescStatic:
      "",
    staticTables: [
      {
        half: true,
        columns: policyTableColumns,
        rows: preTradePolicyRows,
      },
    ],
  },
  {
    mainHeadingStatic: "Post Trade Policies",
    mainGridIdStatic: "post-trade-policies",
    mainHeadingDescStatic:
      "",
    staticTables: [
      {
        half: true,
        columns: policyTableColumns,
        rows: postTradePolicyRows,
      },
    ],
  },
  {
    mainHeadingStatic: "Disclaimer and Confidentiality Notice",
    mainGridIdStatic: "disclaimer",
    mainHeadingDescStatic:
      "The information contained in these documents is confidential, privileged, and only for the understanding of the intended recipient and may not be used, published, or redistributed without prior written consent from Gato Systems.",
    mainHeadingDescStaticTwo:
      "The information is provided with care in these documents. Gato Systems makes no representations and gives no warranties of whatever nature in respect of these documents, including, but not limited to, the accuracy or completeness of any information, facts, and/or opinions contained therein. Gato Systems, its subsidiaries, the directors, employees, and agents cannot be held liable for the use of, and reliance on the information provided in these documents.",
  },
];

export default griskApiData;
